import { ChangeDetectorRef, Component, HostListener, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSelect } from '@angular/material/select';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  ControlDpService,
  ControlDpEmployee,
  ControlDpOccurrence,
  ControlDpRole,
  ControlDpHistory,
  ControlDpAttachment,
  ControlDpImport,
  ControlDpDigitalAdmission,
  ControlDpOccurrenceAttachment,
  ControlDpEmployeeDocumentFile,
  ControlDpEmployeeDocumentsResponse,
  ControlDpAlertRule,
  ControlDpAdmissionAlertRoute,
  ControlDpFeedbackTemplate,
  ControlDpEmployeeFeedback,
  ControlDpFeedbackDashboard
} from '../../services/controldp.service';
import { UserSessionService } from '../../services/user-session.service';
import { OrgStructureService } from '../../services/org-structure.service';
import { ControlQuestionService, ControlQuestion, ControlQuestionOption } from '../../services/controlquestion.service';
import { MoodService } from '../../services/mood.service';
import { API_BASE_URL } from '../../services/api-base';
import { forkJoin, of, firstValueFrom } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { ChartConfiguration, ChartData } from 'chart.js';

type TabKey = 'dashboard' | 'collaborators' | 'imports' | 'digitalAdmissions';
type EmployeeDocsTab = 'controldocs' | 'attachments';
type EmployeeDocsCategoryKey = 'RG_CPF' | 'ADDRESS' | 'BANK' | 'OTHERS';
type EmployeeModalSectionKey = 'personal' | 'contact' | 'corporate' | 'structure' | 'documents';
type OccurrenceModalSectionKey = 'data' | 'attachments' | 'history';
type DigitalAdmissionFeedbackState = 'idle' | 'loading' | 'success' | 'error';
type DigitalAdmissionFieldKey = 'cpf' | 'full_name' | 'hired_at' | 'personal_email';
type EmployeeDateFieldKey = 'birth_date' | 'hired_at' | 'experience_45_days' | 'experience_90_days';
type OccurrenceDateFieldKey = 'start_date' | 'end_date';
type AlertLocationType = 'CEP' | 'ADDRESS';
type AlertValidationUnit = 'RADIUS_KM' | 'TRAVEL_TIME';
type AlertTravelMode = 'DRIVING' | 'WALKING' | 'TRANSIT';
type AlertConditionOperator = 'GREATER_THAN' | 'LESS_THAN' | 'BETWEEN' | 'GREATER_OR_EQUAL' | 'LESS_OR_EQUAL';
type FeedbackQuestionType = 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | 'DROPDOWN' | 'TEXT' | 'NUMBER' | 'DATE';
type FeedbackModalTab = 'cadastro' | 'perguntas' | 'feedbacks';
type RoleModalTab = 'cadastro' | 'cargos';
type EmployeeFeedbackStatus = 'DRAFT' | 'SCHEDULED' | 'APPLIED';
type EmployeeFeedbackTab = 'REGISTRO' | EmployeeFeedbackStatus;
type DashboardFilterKey = 'ALL' | 'COLLABORATOR' | 'THERMOMETER' | 'FEEDBACK';

interface FeedbackOptionDraft {
  id?: number | null;
  label: string;
}

interface FeedbackQuestionDraft {
  id?: number | null;
  title: string;
  description: string;
  question_type: FeedbackQuestionType;
  required: boolean;
  options: FeedbackOptionDraft[];
}

interface EmployeeFeedbackFormDraft {
  feedback_id: number | null;
  feedback_template_id: string;
  notes: string;
  collaborator_notes: string;
  scheduled_for: string;
}

interface EmployeeFeedbackQuestionOption {
  id: number | null;
  label: string;
  order_index: number;
}

interface EmployeeFeedbackQuestion {
  id: number;
  title: string;
  description: string;
  question_type: string;
  required: boolean;
  order_index: number;
  options: EmployeeFeedbackQuestionOption[];
}

type HistoryAttachmentView = {
  name: string;
  path: string;
  created_at?: string | null;
};

type HistoryAlertRouteView = {
  id: string;
  alert_name: string;
  measured_title: string;
  measured_label: string;
  validation_label: string;
  condition_label: string;
  origin_address: string;
  destination_address: string;
  map_path: string;
  directions_url: string;
  created_at?: string | null;
};

type HistoryTimelineItem = {
  id: string;
  action_type: string;
  action_label: string;
  title: string;
  created_at?: string | null;
  summary_lines: string[];
  attachments: HistoryAttachmentView[];
  alert_routes: HistoryAlertRouteView[];
};

type EmployeeDocumentPreview = {
  name: string;
  path: string;
  file_type?: string | null;
  created_at?: string | null;
  source: 'controldocs' | 'attachments';
};

type DashboardLineTooltip = {
  visible: boolean;
  x: number;
  y: number;
  title: string;
  value: string;
};

@Component({
  selector: 'app-controldp',
  templateUrl: './controldp.component.html',
  styleUrls: ['./controldp.component.scss']
})
export class ControldpComponent implements OnInit {
  dashboardLineChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    animation: { duration: 420 },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#15385f',
        titleColor: '#ffffff',
        bodyColor: '#e9f1ff',
        displayColors: true,
        cornerRadius: 10,
        padding: 10
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: '#7f92aa',
          font: { size: 12, weight: 'bold' },
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12
        }
      },
      y: {
        beginAtZero: true,
        grid: { color: '#e7eef8' },
        ticks: {
          color: '#7f92aa',
          font: { size: 11, weight: 600 },
          precision: 0
        }
      }
    }
  };

  private admissionsChartCacheSignature = '';
  private admissionsChartCache: ChartData<'line'> = { labels: [], datasets: [] };
  private moodTrendChartCacheSignature = '';
  private moodTrendChartCache: ChartData<'line'> = { labels: [], datasets: [] };
  private feedbackChartCacheSignature = '';
  private feedbackChartCache: ChartData<'line'> = { labels: [], datasets: [] };

  private readonly apiBase = `${API_BASE_URL}`;
  private readonly tabStorageKey = 'controldp.activeTab';
  private readonly defaultEmployeeAvatar = 'assets/images/person/avatar-default.svg';

  tenantId: number | null = null;
  userId: number | null = null;
  isAdmin = false;
  isMaster = false;

  activeTab: TabKey = 'dashboard';

  dashboard = { total: 0, active: 0, on_leave: 0, terminated: 0, in_experience: 0 };
  feedbackDashboard: ControlDpFeedbackDashboard = this.defaultFeedbackDashboard();
  feedbackDashboardLoading = false;
  feedbackDashboardError = '';
  feedbackDashboardTemplateId = '';
  feedbackDashboardPeriodMonths = 12;

  employees: ControlDpEmployee[] = [];
  roles: ControlDpRole[] = [];
  occurrences: ControlDpOccurrence[] = [];
  dashboardOccurrences: ControlDpOccurrence[] = [];
  history: HistoryTimelineItem[] = [];
  attachments: ControlDpAttachment[] = [];
  employeeDocsByCategory: Record<EmployeeDocsCategoryKey, ControlDpEmployeeDocumentFile[]> = this.createEmptyEmployeeDocsByCategory();
  imports: ControlDpImport[] = [];
  digitalAdmissions: ControlDpDigitalAdmission[] = [];
  alertRules: ControlDpAlertRule[] = [];

  sectors: any[] = [];
  departments: any[] = [];
  locations: any[] = [];

  filterTerm = '';
  filterSectorId = '';
  filterDepartmentId = '';
  dashboardFilter: DashboardFilterKey = 'ALL';
  dashboardFilterTouched = false;
  currentPage = 1;
  digitalAdmissionPage = 1;
  pageSize = 5;
  historyPage = 1;
  historyPageSize = 5;
  selectedEmployee: ControlDpEmployee | null = null;
  loading = false;

  showEmployeeModal = false;
  showOccurrenceModal = false;
  showHistoryModal = false;
  showRoleModal = false;
  showImportModal = false;
  showFilterModal = false;
  showDigitalAdmissionModal = false;
  showAlertModal = false;
  showFeedbackModal = false;
  showEmployeeFeedbackModal = false;
  roleModalTab: RoleModalTab = 'cadastro';
  roleModalTabs: Array<{ value: RoleModalTab; label: string; icon: string }> = [
    { value: 'cadastro', label: 'Cadastro', icon: 'edit_note' },
    { value: 'cargos', label: 'Cargos', icon: 'badge' }
  ];
  employeeFeedbackTarget: ControlDpEmployee | null = null;
  employeeFeedbackTab: EmployeeFeedbackTab = 'REGISTRO';
  employeeFeedbackTabs: Array<{ value: EmployeeFeedbackTab; label: string; icon: string }> = [
    { value: 'REGISTRO', label: 'Registro', icon: 'edit_note' },
    { value: 'DRAFT', label: 'Rascunho', icon: 'drafts' },
    { value: 'SCHEDULED', label: 'Agendados', icon: 'event' },
    { value: 'APPLIED', label: 'Aplicados', icon: 'task_alt' }
  ];
  employeeFeedbackLoading = false;
  employeeFeedbackQuestionsLoading = false;
  employeeFeedbackSaving = false;
  employeeFeedbackError = '';
  employeeFeedbackRecords: ControlDpEmployeeFeedback[] = [];
  employeeFeedbackQuestions: EmployeeFeedbackQuestion[] = [];
  employeeFeedbackAnswers: Record<string, any> = {};
  employeeFeedbackForm: EmployeeFeedbackFormDraft = this.defaultEmployeeFeedbackForm();
  feedbackModalTab: FeedbackModalTab = 'cadastro';
  feedbackSaving = false;
  feedbackEditorLoading = false;
  feedbackError = '';
  feedbackTemplatesLoading = false;
  feedbackTemplatesError = '';
  feedbackTemplates: ControlDpFeedbackTemplate[] = [];
  feedbackEditingTemplate: ControlDpFeedbackTemplate | null = null;
  feedbackEditingSurveyId: number | null = null;

  feedbackForm = this.defaultFeedbackForm();
  feedbackQuestions: FeedbackQuestionDraft[] = [];
  feedbackModalTabs: Array<{ value: FeedbackModalTab; label: string; icon: string }> = [
    { value: 'cadastro', label: 'Cadastro', icon: 'edit_note' },
    { value: 'perguntas', label: 'Perguntas', icon: 'quiz' },
    { value: 'feedbacks', label: 'Feedbacks', icon: 'list_alt' }
  ];
  readonly feedbackSelectAllSectorsValue = '__ALL_SECTORS__';
  readonly feedbackSelectAllDepartmentsValue = '__ALL_DEPARTMENTS__';
  feedbackQuestionTypeOptions: Array<{ value: FeedbackQuestionType; label: string }> = [
    { value: 'SINGLE_CHOICE', label: 'Escolha única' },
    { value: 'MULTIPLE_CHOICE', label: 'Escolha múltipla' },
    { value: 'DROPDOWN', label: 'Lista suspensa' },
    { value: 'TEXT', label: 'Texto livre' },
    { value: 'NUMBER', label: 'Resposta numérica' },
    { value: 'DATE', label: 'Data' }
  ];

  employeeForm: any = this.defaultEmployeeForm();
  occurrenceForm: any = this.defaultOccurrenceForm();
  roleForm = { name: '', base_salary: '' };
  editingRole: ControlDpRole | null = null;
  occurrenceAttachmentFile: File | null = null;
  occurrenceAttachmentName = '';
  occurrenceAttachmentDragging = false;
  readonly certificateModalityOptions = [
    'Técnico',
    'Graduação',
    'Pós',
    'Certificação',
    'Workshop'
  ];
  readonly certificateKnowledgeAreaGroups: Array<{ label: string; options: string[] }> = [
    {
      label: 'Negócios & Gestão',
      options: [
        'Administração',
        'Gestão de Pessoas',
        'Liderança',
        'Gestão de Projetos',
        'Estratégia',
        'Empreendedorismo',
        'Governança'
      ]
    },
    {
      label: 'Tecnologia & Dados',
      options: [
        'Tecnologia da Informação',
        'Desenvolvimento de Software',
        'Segurança da Informação',
        'Dados & Analytics',
        'Inteligência Artificial',
        'Cloud Computing',
        'Infraestrutura'
      ]
    },
    {
      label: 'Operações & Processos',
      options: [
        'Operações',
        'Qualidade',
        'Processos',
        'Melhoria Contínua',
        'Logística',
        'Atendimento'
      ]
    },
    {
      label: 'Comercial & Marketing',
      options: [
        'Vendas',
        'Marketing',
        'Negociação',
        'Experiência do Cliente (CX)',
        'Comunicação'
      ]
    },
    {
      label: 'Financeiro & Compliance',
      options: [
        'Finanças',
        'Controladoria',
        'Compliance',
        'Auditoria',
        'Riscos'
      ]
    },
    {
      label: 'Jurídico & Regulatório',
      options: [
        'Direito Empresarial',
        'LGPD',
        'Regulatório'
      ]
    },
    {
      label: 'Desenvolvimento Pessoal',
      options: [
        'Soft Skills',
        'Inteligência Emocional',
        'Produtividade',
        'Comunicação Interpessoal'
      ]
    }
  ];
  attachmentDragging = false;
  employeeDocsDraggingCategory: EmployeeDocsCategoryKey | null = null;
  employeeDocsUploadingCategory: EmployeeDocsCategoryKey | null = null;
  employeeDocsDeletingFileId: number | null = null;
  employeeDocsLoading = false;
  employeeDocsError = '';
  employeeDocsTab: EmployeeDocsTab = 'controldocs';
  employeeModalSection: EmployeeModalSectionKey | null = 'personal';
  occurrenceModalSection: OccurrenceModalSectionKey | null = 'data';
  occurrenceHistoryLoading = false;
  employeePhotoUploading = false;
  employeePhotoLocalPreviewUrl = '';
  dashboardLineTooltip: DashboardLineTooltip = {
    visible: false,
    x: 0,
    y: 0,
    title: '',
    value: ''
  };
  feedbackLineTooltip: DashboardLineTooltip = {
    visible: false,
    x: 0,
    y: 0,
    title: '',
    value: ''
  };
  employeeDocsPreview: EmployeeDocumentPreview | null = null;
  employeeDocsCategories: Array<{ key: EmployeeDocsCategoryKey; label: string; helper: string }> = [
    { key: 'RG_CPF', label: 'RG e CPF', helper: 'Documento de identificação do colaborador.' },
    { key: 'ADDRESS', label: 'Comprovante de endereço', helper: 'Comprovante residencial atualizado.' },
    { key: 'BANK', label: 'Dados bancários', helper: 'Comprovante de conta para pagamento.' },
    { key: 'OTHERS', label: 'Outros', helper: 'Documentos complementares necessários.' }
  ];
  digitalAdmissionForm: any = this.defaultDigitalAdmissionForm();
  alertRuleForm: any = this.defaultAlertRuleForm();
  editingAlertRuleId: number | null = null;
  alertRuleSubmitAttempted = false;
  alertRuleSaving = false;
  alertRuleTogglingId: number | null = null;
  alertRuleError = '';
  alertRuleMapUrl = '';
  alertRuleMapEmbedUrl: SafeResourceUrl | null = null;
  alertRuleMapLoading = false;
  digitalAdmissionSubmitAttempted = false;
  digitalAdmissionTouched: Record<DigitalAdmissionFieldKey, boolean> = this.defaultDigitalAdmissionTouchedState();
  reactivatingAdmissionEmployeeId: number | null = null;
  dashboardPeriodMonths = 12;
  dashboardLeavePeriodMonths = 6;
  readonly dashboardPeriodOptions = [6, 12, 24];
  readonly dashboardLeavePeriodOptions = [3, 6, 12];
  readonly moodPeriodOptions = [3, 6, 12];
  readonly dashboardFilterOptions: Array<{ value: DashboardFilterKey; label: string }> = [
    { value: 'ALL', label: 'Todos os dashboards' },
    { value: 'COLLABORATOR', label: 'Dashboard colaborador' },
    { value: 'THERMOMETER', label: 'Dashboard termômetro' },
    { value: 'FEEDBACK', label: 'Dashboard feedback' }
  ];
  private readonly dashboardDepartmentPalette = ['#22c2be', '#55d4d1', '#86dce2', '#a8dce6', '#c6e7ef', '#d9eef5'];
  private readonly dashboardGenderPalette = ['#22c2be', '#4267b2', '#f2c84b', '#9aa9bf'];
  private readonly moodOptions = [
    { id: 'excellent', label: 'Excelente', emoji: '😄' },
    { id: 'good', label: 'Bom', emoji: '🙂' },
    { id: 'neutral', label: 'Neutro', emoji: '😐' },
    { id: 'sad', label: 'Triste', emoji: '☹️' },
    { id: 'terrible', label: 'Péssimo', emoji: '😢' }
  ];
  moodSummaryRows: Array<{ id: string; label: string; emoji: string; count: number; percent: number }> = this.moodOptions.map((item) => ({
    id: item.id,
    label: item.label,
    emoji: item.emoji,
    count: 0,
    percent: 0
  }));
  moodSummaryTotal = 0;
  moodSummaryLoading = false;
  moodPeriodMonths = 3;
  moodMonthlyRows: Array<{ month_key: string; label: string; count: number; unique_respondents: number }> = [];
  moodGenderRows: Array<{ key: string; label: string; count: number; percent: number }> = [];
  moodDepartmentRows: Array<{
    label: string;
    count: number;
    percent: number;
    positive_count: number;
    neutral_count: number;
    negative_count: number;
  }> = [];
  moodUniqueRespondents = 0;
  digitalAdmissionFeedbackState: DigitalAdmissionFeedbackState = 'idle';
  digitalAdmissionFeedbackMessage = '';
  digitalAdmissionAlert = { text: '', variant: 'success' as 'success' | 'error' };
  @ViewChild('employeeBirthDatePicker') employeeBirthDatePicker?: MatDatepicker<Date>;
  @ViewChild('employeeHiredDatePicker') employeeHiredDatePicker?: MatDatepicker<Date>;
  @ViewChild('employeeExperience45DatePicker') employeeExperience45DatePicker?: MatDatepicker<Date>;
  @ViewChild('employeeExperience90DatePicker') employeeExperience90DatePicker?: MatDatepicker<Date>;
  @ViewChild('occurrenceStartDatePicker') occurrenceStartDatePicker?: MatDatepicker<Date>;
  @ViewChild('occurrenceEndDatePicker') occurrenceEndDatePicker?: MatDatepicker<Date>;
  @ViewChild('birthDatePicker') birthDatePicker?: MatDatepicker<Date>;
  @ViewChild('hiredAtDatePicker') hiredAtDatePicker?: MatDatepicker<Date>;
  @ViewChild('novoMenuTrigger') novoMenuTrigger?: MatMenuTrigger;
  @ViewChildren(MatSelect) matSelectComponents?: QueryList<MatSelect>;
  private digitalAdmissionAlertTimeout: any;
  private digitalAdmissionFeedbackTimeout: any;
  private alertRuleMapRefreshTimeout: any;
  private employeeFeedbackQuestionRequestId = 0;
  private readonly digitalAdmissionStatuses = new Set(['PENDING_ADMISSION', 'ADMISSION_COMPLETED', 'ADMISSION_CANCELED']);

  importType = 'EMPLOYEES';
  importFileName = '';
  importPreviewRows: Record<string, any>[] = [];
  importHeaders: string[] = [];
  importLoading = false;
  importFeedback = '';
  importErrorRows: Array<{ row_index: number; __error?: string }> = [];
  exportLoading = false;

  importCards = [
    { key: 'EMPLOYEES', title: 'Importar colaboradores', description: 'Base principal de colaboradores.' },
    { key: 'ABSENCES', title: 'Importar faltas', description: 'Registros de ausência por colaborador.' },
    { key: 'LATES', title: 'Importar atrasos', description: 'Registros de atrasos por colaborador.' }
  ];

  downloadImportTemplate(type: string) {
    const template = this.getTemplateDefinition(type);
    const data = [template.headers, template.sample];
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Modelo');
    XLSX.writeFile(workbook, template.fileName);
  }

  maritalStatusOptions = [
    'Solteiro(a)',
    'Casado(a)',
    'União estável',
    'Divorciado(a)',
    'Viúvo(a)',
    'Separado(a)'
  ];
  contractTypeOptions = [
    'CLT',
    'PJ',
    'Estágio',
    'Temporário',
    'Aprendiz',
    'Terceirizado',
    'Outro'
  ];
  bankAccountTypes = ['Corrente', 'Poupança', 'Salário', 'Outro'];
  raceOptions = ['Branca', 'Preta', 'Parda', 'Amarela', 'Indígena', 'Não informado'];
  educationLevelOptions = [
    'Ensino fundamental incompleto',
    'Ensino fundamental completo',
    'Ensino médio incompleto',
    'Ensino médio completo',
    'Ensino superior incompleto',
    'Ensino superior completo',
    'Pós-graduação',
    'Mestrado',
    'Doutorado'
  ];
  alertLocationTypeOptions: Array<{ value: AlertLocationType; label: string }> = [
    { value: 'CEP', label: 'CEP' },
    { value: 'ADDRESS', label: 'Rua / endereço' }
  ];
  alertValidationUnitOptions: Array<{ value: AlertValidationUnit; label: string }> = [
    { value: 'RADIUS_KM', label: 'Raio (km)' },
    { value: 'TRAVEL_TIME', label: 'Tempo de deslocamento (min)' }
  ];
  alertTravelModeOptions: Array<{ value: AlertTravelMode; label: string }> = [
    { value: 'DRIVING', label: 'De carro' },
    { value: 'WALKING', label: 'A pé' },
    { value: 'TRANSIT', label: 'Transporte público' }
  ];
  alertConditionOptions: Array<{ value: AlertConditionOperator; label: string }> = [
    { value: 'GREATER_THAN', label: 'Maior que' },
    { value: 'LESS_THAN', label: 'Menor que' },
    { value: 'BETWEEN', label: 'Entre' },
    { value: 'GREATER_OR_EQUAL', label: 'Maior ou igual a' },
    { value: 'LESS_OR_EQUAL', label: 'Menor ou igual a' }
  ];

  constructor(
    private service: ControlDpService,
    private session: UserSessionService,
    private orgService: OrgStructureService,
    private moodService: MoodService,
    private sanitizer: DomSanitizer,
    private controlquestion: ControlQuestionService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const savedTab = this.getSavedTab();
    if (savedTab) {
      this.activeTab = savedTab;
    }
    const user = this.session.getUser();
    this.tenantId = user?.tenant_id ?? null;
    this.userId = user?.id ?? null;
    this.isAdmin = this.resolveIsAdmin(user);
    this.isMaster = this.resolveIsMaster(user);
    if (this.activeTab === 'imports' && !this.isAdmin) {
      this.activeTab = 'dashboard';
    }
    if (this.activeTab === 'digitalAdmissions' && !this.isMaster) {
      this.activeTab = 'dashboard';
    }
    if (this.tenantId) {
      this.loadDashboard();
      this.loadEmployees();
      this.loadRoles();
      this.loadImports();
      this.loadOrgStructure();
      this.loadDigitalAdmissions();
      this.loadDashboardOccurrences();
      this.loadAlertRules();
      if (this.activeTab === 'dashboard' && this.showDashboardFeedback) {
        this.loadFeedbackTemplates();
        this.loadFeedbackDashboard();
      }
    }
  }

  loadDashboard() {
    if (!this.tenantId) return;
    this.service.getDashboard(this.tenantId).subscribe({
      next: (summary) => (this.dashboard = summary),
      error: () => (this.dashboard = { total: 0, active: 0, on_leave: 0, terminated: 0, in_experience: 0 })
    });
  }

  loadFeedbackDashboard() {
    if (!this.tenantId) {
      this.feedbackDashboard = this.defaultFeedbackDashboard();
      return;
    }
    this.feedbackDashboardLoading = true;
    this.feedbackDashboardError = '';
    const templateId = Number(this.feedbackDashboardTemplateId || 0) || null;
    this.service.getFeedbackDashboard(this.tenantId, templateId, this.feedbackDashboardPeriodMonths).subscribe({
      next: (payload) => {
        this.feedbackDashboard = payload || this.defaultFeedbackDashboard();
        this.feedbackDashboardLoading = false;
      },
      error: () => {
        this.feedbackDashboard = this.defaultFeedbackDashboard();
        this.feedbackDashboardLoading = false;
        this.feedbackDashboardError = 'Não foi possível carregar os indicadores de feedback.';
      }
    });
  }

  loadDashboardOccurrences() {
    if (!this.tenantId) return;
    this.service.listOccurrences(this.tenantId).subscribe({
      next: (rows) => (this.dashboardOccurrences = rows || []),
      error: () => (this.dashboardOccurrences = [])
    });
  }

  loadEmployees() {
    if (!this.tenantId) return;
    this.loading = true;
    this.service.listEmployees(this.tenantId).subscribe({
      next: (rows) => {
        this.employees = rows || [];
        this.loadMoodThermometer(this.moodPeriodMonths);
        this.loading = false;
      },
      error: () => {
        this.employees = [];
        this.loadMoodThermometer(this.moodPeriodMonths);
        this.loading = false;
      }
    });
  }

  loadRoles() {
    if (!this.tenantId) return;
    this.service.listRoles(this.tenantId).subscribe({
      next: (rows) => {
        this.roles = rows || [];
        if (this.showEmployeeModal && this.employeeForm.role_id && this.isSalaryEmpty(this.employeeForm.salary)) {
          this.onRoleChange();
        }
      },
      error: () => (this.roles = [])
    });
  }

  loadImports() {
    if (!this.tenantId) return;
    this.service.listImports(this.tenantId).subscribe({
      next: (rows) => (this.imports = rows || []),
      error: () => (this.imports = [])
    });
  }

  loadDigitalAdmissions() {
    if (!this.tenantId) return;
    this.service.listDigitalAdmissions(this.tenantId).subscribe({
      next: (rows) => (this.digitalAdmissions = rows || []),
      error: () => (this.digitalAdmissions = [])
    });
  }

  loadAlertRules() {
    if (!this.tenantId || !this.isMaster) {
      this.alertRules = [];
      return;
    }
    this.service.listAlertRules(this.tenantId).subscribe({
      next: (rows) => {
        this.alertRules = rows || [];
      },
      error: () => (this.alertRules = [])
    });
  }

  loadOrgStructure() {
    if (!this.tenantId) return;
    this.orgService.getSectors(this.tenantId).subscribe({ next: (rows) => (this.sectors = rows || []) });
    this.orgService.getDepartments(this.tenantId).subscribe({ next: (rows) => (this.departments = rows || []) });
    this.orgService.getLocations(this.tenantId).subscribe({ next: (rows) => (this.locations = rows || []) });
  }

  get availableDepartments() {
    if (!this.employeeForm.sector_id) {
      return this.departments;
    }
    return this.departments.filter((dept) => String(dept.sector_id) === String(this.employeeForm.sector_id));
  }

  get availableFilterDepartments() {
    if (!this.filterSectorId) {
      return this.departments;
    }
    return this.departments.filter((dept) => String(dept.sector_id) === String(this.filterSectorId));
  }

  get availableLocations() {
    if (!this.employeeForm.department_id) {
      return this.locations;
    }
    return this.locations.filter((loc) => String(loc.department_id) === String(this.employeeForm.department_id));
  }

  onSectorChange() {
    this.employeeForm.department_id = '';
    this.employeeForm.location_id = '';
  }

  onDepartmentChange() {
    this.employeeForm.location_id = '';
  }

  onFilterSectorChange() {
    this.filterDepartmentId = '';
    this.currentPage = 1;
    this.digitalAdmissionPage = 1;
  }

  get scopedMoodUserIds() {
    const ids = new Set<number>();
    this.scopedEmployees.forEach((employee) => {
      const raw = Number((employee as any)?.user_id || 0);
      if (Number.isFinite(raw) && raw > 0) {
        ids.add(raw);
      }
    });
    return Array.from(ids);
  }

  get scopedMoodDepartmentIds() {
    const ids = new Set<number>();
    this.scopedEmployees.forEach((employee) => {
      const raw = Number(employee?.department_id || 0);
      if (Number.isFinite(raw) && raw > 0) {
        ids.add(raw);
      }
    });
    return Array.from(ids);
  }

  loadMoodThermometer(months = this.moodPeriodMonths) {
    if (!this.tenantId) {
      this.moodSummaryTotal = 0;
      this.moodSummaryRows = this.moodOptions.map((item) => ({ ...item, count: 0, percent: 0 }));
      this.moodMonthlyRows = [];
      this.moodGenderRows = [];
      this.moodDepartmentRows = [];
      this.moodUniqueRespondents = 0;
      return;
    }
    const scopedUserIds = this.scopedMoodUserIds;
    const scopedDepartmentIds = this.scopedMoodDepartmentIds;
    this.moodSummaryLoading = true;
    this.moodService.getScopedAnalytics(this.tenantId, scopedUserIds, months, scopedDepartmentIds).subscribe({
      next: (result) => {
        const sourceRows = Array.isArray(result?.moods) ? result.moods : [];
        const counts = new Map<string, { count: number; percent: number }>();
        sourceRows.forEach((row: any) => {
          counts.set(String(row?.mood || ''), {
            count: Number(row?.count || 0),
            percent: Number(row?.percent || 0)
          });
        });
        this.moodSummaryTotal = Number(result?.total_responses || 0);
        this.moodSummaryRows = this.moodOptions.map((item) => {
          const found = counts.get(item.id);
          return {
            id: item.id,
            label: item.label,
            emoji: item.emoji,
            count: Number(found?.count || 0),
            percent: Number(found?.percent || 0)
          };
        });
        this.moodMonthlyRows = Array.isArray(result?.monthly) ? result.monthly : [];
        this.moodGenderRows = Array.isArray(result?.by_gender) ? result.by_gender : [];
        this.moodDepartmentRows = Array.isArray(result?.by_department) ? result.by_department : [];
        this.moodUniqueRespondents = Number(result?.unique_respondents || 0);
        this.moodSummaryLoading = false;
      },
      error: () => {
        this.moodSummaryTotal = 0;
        this.moodSummaryRows = this.moodOptions.map((item) => ({ ...item, count: 0, percent: 0 }));
        this.moodMonthlyRows = [];
        this.moodGenderRows = [];
        this.moodDepartmentRows = [];
        this.moodUniqueRespondents = 0;
        this.moodSummaryLoading = false;
      }
    });
  }

  getAgeLabel() {
    if (!this.employeeForm.birth_date) {
      return '';
    }
    const birth = new Date(this.employeeForm.birth_date);
    if (Number.isNaN(birth.getTime())) {
      return '';
    }
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age -= 1;
    }
    return String(age);
  }

  get filteredEmployees() {
    let filtered = this.collaboratorsScopedEmployees;
    if (this.filterTerm) {
      const term = this.filterTerm.toLowerCase();
      filtered = filtered.filter((emp) =>
        [emp.full_name, emp.cpf, emp.role_name].some((value) => (value || '').toLowerCase().includes(term))
      );
    }
    return filtered;
  }

  get filteredDigitalAdmissionEmployees() {
    let filtered = this.digitalAdmissionsScopedEmployees;
    if (this.filterTerm) {
      const term = this.filterTerm.toLowerCase();
      filtered = filtered.filter((emp) =>
        [emp.full_name, emp.cpf, emp.role_name].some((value) => (value || '').toLowerCase().includes(term))
      );
    }
    return filtered;
  }

  get scopedEmployees() {
    let filtered = this.employees;
    if (this.filterSectorId) {
      filtered = filtered.filter((emp) => String(emp.sector_id || '') === String(this.filterSectorId));
    }
    if (this.filterDepartmentId) {
      filtered = filtered.filter((emp) => String(emp.department_id || '') === String(this.filterDepartmentId));
    }
    return filtered;
  }

  get collaboratorsScopedEmployees() {
    return this.scopedEmployees.filter((emp) => !this.isDigitalAdmissionStatus(emp.status));
  }

  get digitalAdmissionsScopedEmployees() {
    return this.scopedEmployees.filter((emp) => this.isDigitalAdmissionStatus(emp.status));
  }

  get dashboardView() {
    if (!this.filterSectorId && !this.filterDepartmentId) {
      return this.dashboard;
    }
    const summary = { total: 0, active: 0, on_leave: 0, terminated: 0, in_experience: 0 };
    this.scopedEmployees.forEach((emp) => {
      summary.total += 1;
      const status = String(emp.status || '').toLowerCase();
      if (status === 'active') summary.active += 1;
      if (status === 'on_leave') summary.on_leave += 1;
      if (status === 'terminated') summary.terminated += 1;
      if (status === 'in_experience') summary.in_experience += 1;
    });
    return summary;
  }

  get experienceSummary() {
    const summary = { exp45: 0, exp90: 0 };
    const today = new Date();
    this.scopedEmployees.forEach((emp) => {
      if (String(emp.status || '').toLowerCase() !== 'active') {
        return;
      }
      if (!emp.hired_at) {
        return;
      }
      const hired = new Date(emp.hired_at);
      if (Number.isNaN(hired.getTime())) {
        return;
      }
      const limit45 = emp.experience_45_days ? new Date(emp.experience_45_days) : this.addDays(hired, 45);
      const limit90 = emp.experience_90_days ? new Date(emp.experience_90_days) : this.addDays(hired, 90);
      if (!Number.isNaN(limit45.getTime()) && today <= limit45) {
        summary.exp45 += 1;
        return;
      }
      if (!Number.isNaN(limit90.getTime()) && today <= limit90) {
        summary.exp90 += 1;
      }
    });
    return summary;
  }

  get admissionsByMonth() {
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const now = new Date();
    const buckets: Array<{ key: string; label: string; count: number }> = [];
    const period = Math.max(this.dashboardPeriodMonths, 1);
    for (let i = period - 1; i >= 0; i -= 1) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      const label = `${monthNames[date.getMonth()]} ${String(date.getFullYear()).slice(-2)}`;
      buckets.push({ key, label, count: 0 });
    }
    const bucketMap = new Map(buckets.map((bucket) => [bucket.key, bucket]));
    this.scopedEmployees.forEach((emp) => {
      if (!emp.hired_at) {
        return;
      }
      const hired = new Date(emp.hired_at);
      if (Number.isNaN(hired.getTime())) {
        return;
      }
      const key = `${hired.getFullYear()}-${hired.getMonth()}`;
      const bucket = bucketMap.get(key);
      if (bucket) {
        bucket.count += 1;
      }
    });
    return buckets;
  }

  get admissionsMax() {
    return this.admissionsByMonth.reduce((max, item) => Math.max(max, item.count), 0);
  }

  get admissionsTotal() {
    return this.admissionsByMonth.reduce((sum, item) => sum + item.count, 0);
  }

  get sectorDistribution() {
    return this.aggregateDistribution(this.scopedEmployees, 'sector_name', 'Sem setor');
  }

  get sectorDistributionMax() {
    return this.sectorDistribution.reduce((max, item) => Math.max(max, item.count), 0);
  }

  get departmentDistribution() {
    return this.aggregateDistribution(this.scopedEmployees, 'department_name', 'Sem departamento');
  }

  get departmentDistributionMax() {
    return this.departmentDistribution.reduce((max, item) => Math.max(max, item.count), 0);
  }

  get hiresLast30Days() {
    const now = new Date();
    const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
    return this.scopedEmployees.filter((emp) => {
      if (!emp.hired_at) return false;
      const hired = new Date(emp.hired_at);
      return !Number.isNaN(hired.getTime()) && hired >= cutoff;
    }).length;
  }

  get averageTenureLabel() {
    const now = new Date();
    const tenures = this.scopedEmployees
      .map((emp) => (emp.hired_at ? new Date(emp.hired_at) : null))
      .filter((date) => date && !Number.isNaN(date.getTime())) as Date[];
    if (!tenures.length) {
      return '—';
    }
    const totalDays = tenures.reduce((sum, date) => sum + this.diffInDays(date, now), 0);
    const avgDays = Math.round(totalDays / tenures.length);
    const months = Math.floor(avgDays / 30);
    if (months < 1) {
      return `${avgDays} dias`;
    }
    return `${months} meses`;
  }

  get activeRateLabel() {
    const total = this.scopedEmployees.length;
    if (!total) {
      return '0%';
    }
    const rate = Math.round((this.dashboardView.active / total) * 100);
    return `${rate}%`;
  }

  setDashboardPeriod(months: number) {
    this.dashboardPeriodMonths = months;
  }

  setDashboardLeavePeriod(months: number) {
    this.dashboardLeavePeriodMonths = months;
  }

  get dashboardActiveRateNumber() {
    const total = this.dashboardView.total;
    if (!total) {
      return 0;
    }
    return Number(((this.dashboardView.active / total) * 100).toFixed(1));
  }

  get dashboardInactiveCount() {
    return Math.max(this.dashboardView.total - this.dashboardView.active, 0);
  }

  get dashboardInactiveRateNumber() {
    const total = this.dashboardView.total;
    if (!total) {
      return 0;
    }
    return Number(((this.dashboardInactiveCount / total) * 100).toFixed(1));
  }

  get dashboardInactiveRateLabel() {
    return `${String(this.dashboardInactiveRateNumber.toFixed(1)).replace('.', ',')}%`;
  }

  get dashboardDistributionDonutStyle() {
    const active = this.dashboardActiveRateNumber;
    const inactive = Math.max(100 - active, 0);
    return `conic-gradient(#24c2be 0% ${active}%, #d8eef3 ${active}% ${active + inactive}%, #f2f6fb ${active + inactive}% 100%)`;
  }

  get dashboardDepartmentRows() {
    return (this.departmentDistribution || []).map((row, index) => ({
      ...row,
      color: this.dashboardDepartmentPalette[index % this.dashboardDepartmentPalette.length]
    }));
  }

  get dashboardDepartmentDonutStyle() {
    if (!this.dashboardDepartmentRows.length) {
      return 'conic-gradient(#e7eef8 0% 100%)';
    }
    let current = 0;
    const parts = this.dashboardDepartmentRows.map((row) => {
      const start = current;
      current += row.percent;
      return `${row.color} ${start}% ${Math.min(current, 100)}%`;
    });
    if (current < 100) {
      parts.push(`#eef3fb ${current}% 100%`);
    }
    return `conic-gradient(${parts.join(', ')})`;
  }

  get dashboardDepartmentTop() {
    return this.dashboardDepartmentRows[0] || {
      label: 'Sem dados',
      count: 0,
      percent: 0,
      color: '#e7eef8'
    };
  }

  get dashboardGenderRows() {
    const labels: Record<string, string> = {
      F: 'Feminino',
      M: 'Masculino',
      OTHER: 'Outro',
      NOT_INFORMED: 'Não informado'
    };
    const total = this.scopedEmployees.length || 1;
    const map = new Map<string, number>();
    this.scopedEmployees.forEach((employee) => {
      const raw = String(employee.gender || '').toUpperCase();
      const key = raw === 'F' || raw === 'M' || raw === 'OTHER' ? raw : 'NOT_INFORMED';
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([key, count], index) => ({
        key,
        label: labels[key] || 'Não informado',
        count,
        percent: Math.round((count / total) * 100),
        color: this.dashboardGenderPalette[index % this.dashboardGenderPalette.length]
      }))
      .sort((a, b) => b.count - a.count);
  }

  get dashboardGenderDonutStyle() {
    if (!this.dashboardGenderRows.length) {
      return 'conic-gradient(#e7eef8 0% 100%)';
    }
    let current = 0;
    const parts = this.dashboardGenderRows.map((row) => {
      const start = current;
      current += row.percent;
      return `${row.color} ${start}% ${Math.min(current, 100)}%`;
    });
    if (current < 100) {
      parts.push(`#eef3fb ${current}% 100%`);
    }
    return `conic-gradient(${parts.join(', ')})`;
  }

  get dashboardAgeRangeRows() {
    const now = new Date();
    const rows = [
      { label: '18-24', min: 18, max: 24, count: 0 },
      { label: '25-34', min: 25, max: 34, count: 0 },
      { label: '35-44', min: 35, max: 44, count: 0 },
      { label: '45-54', min: 45, max: 54, count: 0 },
      { label: '55+', min: 55, max: 200, count: 0 }
    ];

    this.scopedEmployees.forEach((employee) => {
      const birthDate = this.parseInputDate(employee.birth_date);
      if (!birthDate) {
        return;
      }
      let age = now.getFullYear() - birthDate.getFullYear();
      const monthDiff = now.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birthDate.getDate())) {
        age -= 1;
      }
      const bucket = rows.find((row) => age >= row.min && age <= row.max);
      if (bucket) {
        bucket.count += 1;
      }
    });

    return rows;
  }

  get dashboardAgeRangeMax() {
    return this.dashboardAgeRangeRows.reduce((max, row) => Math.max(max, row.count), 0);
  }

  get admissionsSeriesValues() {
    return this.admissionsByMonth.map((item) => item.count);
  }

  get admissionsTrendSeriesValues() {
    const values = this.admissionsSeriesValues;
    if (!values.length) {
      return [];
    }
    return values.map((_, index) => {
      const current = values[index] || 0;
      const prev = values[index - 1] || current;
      const next = values[index + 1] || current;
      return Number(((prev + current + next) / 3).toFixed(2));
    });
  }

  get admissionsAveragePerMonth() {
    const values = this.admissionsSeriesValues;
    if (!values.length) {
      return 0;
    }
    const total = values.reduce((sum, value) => sum + value, 0);
    return Number((total / values.length).toFixed(2));
  }

  get admissionsAveragePerMonthLabel() {
    return this.admissionsAveragePerMonth.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });
  }

  get admissionsLineChartData(): ChartData<'line'> {
    const labels = this.admissionsByMonth.map((item) => item.label);
    const admissions = this.admissionsSeriesValues;
    const trend = this.admissionsTrendSeriesValues;
    const avg = this.admissionsAveragePerMonth;
    const signature = JSON.stringify({ labels, admissions, trend, avg });
    if (signature === this.admissionsChartCacheSignature) {
      return this.admissionsChartCache;
    }
    this.admissionsChartCacheSignature = signature;
    this.admissionsChartCache = {
      labels,
      datasets: [
        {
          label: 'Admissões',
          data: admissions,
          borderColor: '#1ec2bf',
          backgroundColor: 'rgba(30, 194, 191, 0.16)',
          pointBackgroundColor: '#1ec2bf',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 5,
          borderWidth: 3,
          fill: true,
          tension: 0.35
        },
        {
          label: 'Tendência',
          data: trend,
          borderColor: '#f0cb59',
          backgroundColor: 'rgba(240, 203, 89, 0.06)',
          pointBackgroundColor: '#f0cb59',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 3.4,
          pointHoverRadius: 4.5,
          borderWidth: 2.5,
          fill: false,
          tension: 0.35
        },
        {
          label: 'Média',
          data: labels.map(() => avg),
          borderColor: '#8ea2bf',
          borderDash: [6, 5],
          pointRadius: 0,
          pointHoverRadius: 0,
          borderWidth: 2,
          fill: false,
          tension: 0
        }
      ]
    };
    return this.admissionsChartCache;
  }

  get dashboardLineMaxValue() {
    return Math.max(...this.admissionsSeriesValues, ...this.admissionsTrendSeriesValues, 1);
  }

  dashboardLinePointList(values: number[]) {
    return this.dashboardLineDots(values)
      .map((point) => `${point.x},${point.y}`)
      .join(' ');
  }

  dashboardLineYByValue(value: number) {
    const chartTop = 18;
    const chartBottom = 192;
    const maxValue = this.dashboardLineMaxValue || 1;
    const y = chartBottom - (Number(value || 0) / maxValue) * (chartBottom - chartTop);
    return Number(y.toFixed(2));
  }

  dashboardLineDotTooltip(
    point: { label: string; value: number },
    series: 'admissions' | 'trend'
  ) {
    if (series === 'admissions') {
      return `${point.label}: ${point.value} admissões`;
    }
    return `${point.label}: tendência ${point.value.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    })}`;
  }

  dashboardLinePointEnter(
    event: MouseEvent,
    point: { label: string; value: number },
    series: 'admissions' | 'trend'
  ) {
    this.updateDashboardLineTooltip(event, point, series);
  }

  dashboardLinePointMove(
    event: MouseEvent,
    point: { label: string; value: number },
    series: 'admissions' | 'trend'
  ) {
    this.updateDashboardLineTooltip(event, point, series);
  }

  dashboardLinePointLeave() {
    this.dashboardLineTooltip.visible = false;
  }

  get dashboardLineYAxisTicks() {
    const max = Math.max(1, Math.ceil(this.dashboardLineMaxValue));
    const divisions = 4;
    const step = max / divisions;
    const ticks: Array<{ label: string; value: number; y: number }> = [];
    for (let i = 0; i <= divisions; i += 1) {
      const value = Number((max - i * step).toFixed(1));
      const pretty = Number.isInteger(value) ? `${value}` : value.toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      });
      ticks.push({
        label: pretty,
        value,
        y: this.dashboardLineYByValue(value)
      });
    }
    return ticks;
  }

  dashboardLineDots(values: number[]) {
    const points: Array<{ x: number; y: number; value: number; label: string }> = [];
    if (!values.length) {
      return points;
    }
    const widthStart = 36;
    const widthEnd = 616;
    const chartTop = 18;
    const chartBottom = 192;
    const maxValue = this.dashboardLineMaxValue;
    const step = values.length > 1 ? (widthEnd - widthStart) / (values.length - 1) : 0;
    values.forEach((value, index) => {
      const x = widthStart + step * index;
      const y = chartBottom - (Number(value || 0) / maxValue) * (chartBottom - chartTop);
      points.push({
        x: Number(x.toFixed(2)),
        y: Number(y.toFixed(2)),
        value: Number(value || 0),
        label: this.admissionsByMonth[index]?.label || ''
      });
    });
    return points;
  }

  private updateDashboardLineTooltip(
    event: MouseEvent,
    point: { label: string; value: number },
    series: 'admissions' | 'trend'
  ) {
    const target = event.currentTarget as SVGCircleElement | null;
    const container = target?.closest('.dashboard-v2-line-chart') as HTMLElement | null;
    if (!container) {
      this.dashboardLineTooltip.visible = false;
      return;
    }
    const rect = container.getBoundingClientRect();
    let x = event.clientX - rect.left + 14;
    let y = event.clientY - rect.top - 12;
    x = Math.max(90, Math.min(x, rect.width - 20));
    y = Math.max(26, Math.min(y, rect.height - 20));
    const isAdmissions = series === 'admissions';
    this.dashboardLineTooltip = {
      visible: true,
      x,
      y,
      title: `${isAdmissions ? 'Admissões' : 'Tendência'} · ${point.label}`,
      value: isAdmissions
        ? `${point.value} contratação(ões)`
        : `${point.value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} (média móvel)`
    };
  }

  get showDashboardCollaborator() {
    if (!this.dashboardFilterTouched) {
      return true;
    }
    return this.dashboardFilter === 'ALL' || this.dashboardFilter === 'COLLABORATOR';
  }

  get showDashboardThermometer() {
    if (!this.dashboardFilterTouched) {
      return false;
    }
    return this.dashboardFilter === 'ALL' || this.dashboardFilter === 'THERMOMETER';
  }

  get showDashboardFeedback() {
    if (!this.dashboardFilterTouched) {
      return false;
    }
    return this.dashboardFilter === 'ALL' || this.dashboardFilter === 'FEEDBACK';
  }

  get selectedDashboardFeedbackTemplateLabel() {
    if (!this.feedbackDashboardTemplateId) {
      return 'Todos os feedbacks';
    }
    const match = this.feedbackTemplates.find(
      (template) => String(template.id) === String(this.feedbackDashboardTemplateId)
    );
    return match?.title || 'Template selecionado';
  }

  get scopedMoodEmployeeTotal() {
    return this.scopedEmployees.length;
  }

  get moodResponseRateLabel() {
    const total = this.scopedMoodEmployeeTotal;
    if (!total) {
      return '0%';
    }
    const rate = Number(((this.moodUniqueRespondents / total) * 100).toFixed(1));
    return `${String(rate).replace('.', ',')}%`;
  }

  get moodGenderMax() {
    return this.moodGenderRows.reduce((max, item) => Math.max(max, Number(item.count || 0)), 0);
  }

  get moodDepartmentMax() {
    return this.moodDepartmentRows.reduce((max, item) => Math.max(max, Number(item.count || 0)), 0);
  }

  getDepartmentMoodSegmentPercent(
    item: { count: number; positive_count: number; neutral_count: number; negative_count: number },
    segment: 'positive' | 'neutral' | 'negative'
  ) {
    const total = Number(item?.count || 0);
    if (!total) {
      return 0;
    }
    const value = segment === 'positive'
      ? Number(item?.positive_count || 0)
      : segment === 'neutral'
        ? Number(item?.neutral_count || 0)
        : Number(item?.negative_count || 0);
    return Number(((value / total) * 100).toFixed(1));
  }

  moodColor(moodId: string) {
    const palette: Record<string, string> = {
      excellent: '#11b981',
      good: '#22c6be',
      neutral: '#f2c94c',
      sad: '#fb923c',
      terrible: '#ef4444'
    };
    return palette[String(moodId || '').toLowerCase()] || '#22c6be';
  }

  get moodDonutStyle() {
    const rows = (this.moodSummaryRows || []).filter((row) => Number(row.percent || 0) > 0);
    if (!rows.length) {
      return 'conic-gradient(#e7eef8 0% 100%)';
    }
    let current = 0;
    const parts = rows.map((row) => {
      const start = current;
      current += Number(row.percent || 0);
      return `${this.moodColor(row.id)} ${start}% ${Math.min(current, 100)}%`;
    });
    if (current < 100) {
      parts.push(`#eef3fb ${current}% 100%`);
    }
    return `conic-gradient(${parts.join(', ')})`;
  }

  get moodGenderDonutStyle() {
    const rows = (this.moodGenderRows || []).filter((row) => Number(row.percent || 0) > 0);
    if (!rows.length) {
      return 'conic-gradient(#e7eef8 0% 100%)';
    }
    let current = 0;
    const parts = rows.map((row) => {
      const start = current;
      current += Number(row.percent || 0);
      return `${this.moodGenderColor(row.key)} ${start}% ${Math.min(current, 100)}%`;
    });
    if (current < 100) {
      parts.push(`#eef3fb ${current}% 100%`);
    }
    return `conic-gradient(${parts.join(', ')})`;
  }

  moodGenderColor(key: string) {
    const normalized = String(key || '').toUpperCase();
    if (normalized === 'F') return '#5C83FF';
    if (normalized === 'M') return '#21C2BA';
    if (normalized === 'OTHER') return '#34D399';
    if (normalized === 'NOT_INFORMED') return '#F6C85F';
    return '#9CA3AF';
  }

  get moodTrendMaxValue() {
    return Math.max(
      ...this.moodMonthlyRows.map((item) => Math.max(Number(item.count || 0), Number(item.unique_respondents || 0))),
      1
    );
  }

  moodTrendYByValue(value: number) {
    const chartTop = 26;
    const chartBottom = 186;
    const maxValue = this.moodTrendMaxValue || 1;
    const y = chartBottom - (Number(value || 0) / maxValue) * (chartBottom - chartTop);
    return Number(y.toFixed(2));
  }

  get moodTrendDots() {
    const points: Array<{ x: number; y: number; value: number; label: string }> = [];
    const values = this.moodMonthlyRows || [];
    if (!values.length) return points;
    const widthStart = 36;
    const widthEnd = 620;
    const step = values.length > 1 ? (widthEnd - widthStart) / (values.length - 1) : 0;
    values.forEach((item, index) => {
      const count = Number(item?.count || 0);
      const x = widthStart + step * index;
      const y = this.moodTrendYByValue(count);
      points.push({ x: Number(x.toFixed(2)), y, value: count, label: String(item?.label || '') });
    });
    return points;
  }

  get moodTrendLinePointList() {
    return this.moodTrendDots.map((point) => `${point.x},${point.y}`).join(' ');
  }

  get moodTrendRespondentDots() {
    const points: Array<{ x: number; y: number; value: number; label: string }> = [];
    const values = this.moodMonthlyRows || [];
    if (!values.length) return points;
    const widthStart = 36;
    const widthEnd = 620;
    const step = values.length > 1 ? (widthEnd - widthStart) / (values.length - 1) : 0;
    values.forEach((item, index) => {
      const count = Number(item?.unique_respondents || 0);
      const x = widthStart + step * index;
      const y = this.moodTrendYByValue(count);
      points.push({ x: Number(x.toFixed(2)), y, value: count, label: String(item?.label || '') });
    });
    return points;
  }

  get moodTrendRespondentPointList() {
    return this.moodTrendRespondentDots.map((point) => `${point.x},${point.y}`).join(' ');
  }

  get moodTrendLineChartData(): ChartData<'line'> {
    const labels = (this.moodMonthlyRows || []).map((item) => item.label);
    const totals = (this.moodMonthlyRows || []).map((item) => Number(item.count || 0));
    const respondents = (this.moodMonthlyRows || []).map((item) => Number(item.unique_respondents || 0));
    const signature = JSON.stringify({ labels, totals, respondents });
    if (signature === this.moodTrendChartCacheSignature) {
      return this.moodTrendChartCache;
    }
    this.moodTrendChartCacheSignature = signature;
    this.moodTrendChartCache = {
      labels,
      datasets: [
        {
          label: 'Respostas',
          data: totals,
          borderColor: '#22c6be',
          backgroundColor: 'rgba(34, 198, 190, 0.18)',
          pointBackgroundColor: '#22c6be',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 5,
          borderWidth: 3,
          fill: true,
          tension: 0.35
        },
        {
          label: 'Colaboradores que responderam',
          data: respondents,
          borderColor: '#f0cb59',
          backgroundColor: 'rgba(240, 203, 89, 0.08)',
          pointBackgroundColor: '#f0cb59',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 3.5,
          pointHoverRadius: 4.5,
          borderWidth: 2.5,
          fill: false,
          tension: 0.35
        }
      ]
    };
    return this.moodTrendChartCache;
  }

  get moodTrendAreaPath() {
    const dots = this.moodTrendDots;
    if (!dots.length) return '';
    const chartBottom = 186;
    const first = dots[0];
    const lines = dots.map((point) => `L ${point.x} ${point.y}`).join(' ');
    return `M ${first.x} ${chartBottom} ${lines} L ${dots[dots.length - 1].x} ${chartBottom} Z`;
  }

  setMoodPeriod(months: number) {
    this.moodPeriodMonths = months;
    this.loadMoodThermometer(months);
  }

  private updateFeedbackLineTooltip(
    event: MouseEvent,
    point: { label: string; value: number },
    series: 'applied' | 'trend'
  ) {
    const target = event.currentTarget as SVGCircleElement | null;
    const container = target?.closest('.dashboard-v2-line-chart') as HTMLElement | null;
    if (!container) {
      this.feedbackLineTooltip.visible = false;
      return;
    }
    const rect = container.getBoundingClientRect();
    let x = event.clientX - rect.left + 14;
    let y = event.clientY - rect.top - 12;
    x = Math.max(90, Math.min(x, rect.width - 20));
    y = Math.max(26, Math.min(y, rect.height - 20));
    const isApplied = series === 'applied';
    this.feedbackLineTooltip = {
      visible: true,
      x,
      y,
      title: `${isApplied ? 'Feedbacks aplicados' : 'Tendência'} · ${point.label}`,
      value: isApplied
        ? `${point.value} aplicado(s)`
        : `${point.value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} (média móvel)`
    };
  }

  get admissionsQuarterlySeries() {
    const now = new Date();
    const currentQuarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
    const quarters: Array<{ key: string; label: string; admissions: number; inactive: number }> = [];

    for (let i = 5; i >= 0; i -= 1) {
      const date = new Date(now.getFullYear(), currentQuarterStartMonth - i * 3, 1);
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      const year = date.getFullYear();
      const key = `${year}-${quarter}`;
      quarters.push({
        key,
        label: `Q${quarter}/${String(year).slice(-2)}`,
        admissions: 0,
        inactive: 0
      });
    }

    const quarterMap = new Map(quarters.map((item) => [item.key, item]));
    this.scopedEmployees.forEach((employee) => {
      if (!employee?.hired_at) {
        return;
      }
      const hiredDate = new Date(employee.hired_at);
      if (Number.isNaN(hiredDate.getTime())) {
        return;
      }
      const quarter = Math.floor(hiredDate.getMonth() / 3) + 1;
      const key = `${hiredDate.getFullYear()}-${quarter}`;
      const bucket = quarterMap.get(key);
      if (!bucket) {
        return;
      }
      bucket.admissions += 1;
      const status = String(employee.status || '').toUpperCase();
      if (status === 'INACTIVE' || status === 'TERMINATED') {
        bucket.inactive += 1;
      }
    });

    return quarters;
  }

  get admissionsQuarterlyMax() {
    const maxAdmission = this.admissionsQuarterlySeries.reduce((max, item) => Math.max(max, item.admissions), 0);
    const maxInactive = this.admissionsQuarterlySeries.reduce((max, item) => Math.max(max, item.inactive), 0);
    return Math.max(maxAdmission, maxInactive, 1);
  }

  get dashboardScopedOccurrenceRows() {
    if (!this.dashboardOccurrences.length || !this.scopedEmployees.length) {
      return [];
    }
    const employeeIds = new Set(this.scopedEmployees.map((employee) => employee.id));
    return this.dashboardOccurrences.filter((occurrence) => employeeIds.has(occurrence.employee_id));
  }

  get dashboardLeaveReasonRows() {
    const months = Math.max(this.dashboardLeavePeriodMonths, 1);
    const now = new Date();
    const cutoff = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1);
    const counter = new Map<string, number>();

    this.dashboardScopedOccurrenceRows.forEach((occurrence) => {
      if (String(occurrence.type || '').toUpperCase() !== 'LEAVE') {
        return;
      }
      const date = this.parseInputDate(occurrence.start_date || occurrence.created_at || occurrence.end_date);
      if (!date || date < cutoff) {
        return;
      }
      const reason = this.resolveLeaveReasonLabel(occurrence);
      counter.set(reason, (counter.get(reason) || 0) + 1);
    });

    return Array.from(counter.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }

  get dashboardLeaveReasonMax() {
    return this.dashboardLeaveReasonRows.reduce((max, row) => Math.max(max, row.count), 0);
  }

  get dashboardBirthdaysThisMonth() {
    const monthNow = new Date().getMonth();
    const rows = this.scopedEmployees
      .map((employee) => {
        const date = this.parseInputDate(employee.birth_date);
        if (!date || date.getMonth() !== monthNow) {
          return null;
        }
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        let age = new Date().getFullYear() - date.getFullYear();
        const current = new Date();
        if (current.getMonth() < date.getMonth() || (current.getMonth() === date.getMonth() && current.getDate() < date.getDate())) {
          age -= 1;
        }
        return {
          full_name: employee.full_name || 'Colaborador',
          birth_day: day,
          birth_month: month,
          age_label: age > 0 ? `${age} anos` : 'idade n/d',
          initials: (employee.full_name || 'C')
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part.charAt(0).toUpperCase())
            .join('')
        };
      })
      .filter(Boolean) as Array<{ full_name: string; birth_day: string; birth_month: string; age_label: string; initials: string }>;

    rows.sort((a, b) => Number(a.birth_day) - Number(b.birth_day));
    return rows.slice(0, 8);
  }

  get feedbackDashboardSummary() {
    return this.feedbackDashboard?.summary || this.defaultFeedbackDashboard().summary;
  }

  get feedbackAppliedRateLabel() {
    return `${String(Number(this.feedbackDashboardSummary.applied_rate || 0).toFixed(1)).replace('.', ',')}%`;
  }

  get feedbackMonthlyRows() {
    return Array.isArray(this.feedbackDashboard?.monthly) ? this.feedbackDashboard.monthly : [];
  }

  get feedbackMonthlyMax() {
    return this.feedbackMonthlyRows.reduce((max, row) => Math.max(max, Number(row.total || 0)), 0);
  }

  get feedbackSeriesTotalValues() {
    return this.feedbackMonthlyRows.map((item) => Number(item.total || 0));
  }

  get feedbackSeriesAppliedValues() {
    return this.feedbackMonthlyRows.map((item) => Number(item.applied || 0));
  }

  get feedbackTrendSeriesValues() {
    const values = this.feedbackSeriesAppliedValues;
    if (!values.length) {
      return [];
    }
    return values.map((_, index) => {
      const current = values[index] || 0;
      const prev = values[index - 1] || current;
      const next = values[index + 1] || current;
      return Number(((prev + current + next) / 3).toFixed(2));
    });
  }

  get feedbackAveragePerMonth() {
    const values = this.feedbackSeriesAppliedValues;
    if (!values.length) {
      return 0;
    }
    const total = values.reduce((sum, value) => sum + value, 0);
    return Number((total / values.length).toFixed(2));
  }

  get feedbackAveragePerMonthLabel() {
    return this.feedbackAveragePerMonth.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });
  }

  get feedbackLineChartData(): ChartData<'line'> {
    const labels = this.feedbackMonthlyRows.map((item) => item.label);
    const applied = this.feedbackSeriesAppliedValues;
    const trend = this.feedbackTrendSeriesValues;
    const avg = this.feedbackAveragePerMonth;
    const signature = JSON.stringify({ labels, applied, trend, avg });
    if (signature === this.feedbackChartCacheSignature) {
      return this.feedbackChartCache;
    }
    this.feedbackChartCacheSignature = signature;
    this.feedbackChartCache = {
      labels,
      datasets: [
        {
          label: 'Feedbacks aplicados',
          data: applied,
          borderColor: '#1ec2bf',
          backgroundColor: 'rgba(30, 194, 191, 0.16)',
          pointBackgroundColor: '#1ec2bf',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 5,
          borderWidth: 3,
          fill: true,
          tension: 0.35
        },
        {
          label: 'Tendência',
          data: trend,
          borderColor: '#f0cb59',
          backgroundColor: 'rgba(240, 203, 89, 0.08)',
          pointBackgroundColor: '#f0cb59',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 3.4,
          pointHoverRadius: 4.5,
          borderWidth: 2.5,
          fill: false,
          tension: 0.35
        },
        {
          label: 'Média',
          data: labels.map(() => avg),
          borderColor: '#8ea2bf',
          borderDash: [6, 5],
          pointRadius: 0,
          pointHoverRadius: 0,
          borderWidth: 2,
          fill: false,
          tension: 0
        }
      ]
    };
    return this.feedbackChartCache;
  }

  get feedbackLineMaxValue() {
    return Math.max(...this.feedbackSeriesAppliedValues, ...this.feedbackTrendSeriesValues, 1);
  }

  feedbackLinePointList(values: number[]) {
    return this.feedbackLineDots(values)
      .map((point) => `${point.x},${point.y}`)
      .join(' ');
  }

  feedbackLineYByValue(value: number) {
    const chartTop = 18;
    const chartBottom = 192;
    const maxValue = this.feedbackLineMaxValue || 1;
    const y = chartBottom - (Number(value || 0) / maxValue) * (chartBottom - chartTop);
    return Number(y.toFixed(2));
  }

  get feedbackLineYAxisTicks() {
    const max = Math.max(1, Math.ceil(this.feedbackLineMaxValue));
    const divisions = 4;
    const step = max / divisions;
    const ticks: Array<{ label: string; value: number; y: number }> = [];
    for (let i = 0; i <= divisions; i += 1) {
      const value = Number((max - i * step).toFixed(1));
      const pretty = Number.isInteger(value) ? `${value}` : value.toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      });
      ticks.push({
        label: pretty,
        value,
        y: this.feedbackLineYByValue(value)
      });
    }
    return ticks;
  }

  feedbackLineDots(values: number[]) {
    const points: Array<{ x: number; y: number; value: number; label: string }> = [];
    if (!values.length) {
      return points;
    }
    const widthStart = 36;
    const widthEnd = 616;
    const chartTop = 18;
    const chartBottom = 192;
    const maxValue = this.feedbackLineMaxValue;
    const step = values.length > 1 ? (widthEnd - widthStart) / (values.length - 1) : 0;
    values.forEach((value, index) => {
      const x = widthStart + step * index;
      const y = chartBottom - (Number(value || 0) / maxValue) * (chartBottom - chartTop);
      points.push({
        x: Number(x.toFixed(2)),
        y: Number(y.toFixed(2)),
        value: Number(value || 0),
        label: this.feedbackMonthlyRows[index]?.label || ''
      });
    });
    return points;
  }

  feedbackLinePointEnter(
    event: MouseEvent,
    point: { label: string; value: number },
    series: 'applied' | 'trend'
  ) {
    this.updateFeedbackLineTooltip(event, point, series);
  }

  feedbackLinePointMove(
    event: MouseEvent,
    point: { label: string; value: number },
    series: 'applied' | 'trend'
  ) {
    this.updateFeedbackLineTooltip(event, point, series);
  }

  feedbackLinePointLeave() {
    this.feedbackLineTooltip.visible = false;
  }

  get feedbackTemplateRows() {
    const rows = Array.isArray(this.feedbackDashboard?.templates) ? this.feedbackDashboard.templates : [];
    return rows
      .slice()
      .sort((a, b) => Number(b.total || 0) - Number(a.total || 0))
      .slice(0, 8);
  }

  get feedbackTemplateMax() {
    return this.feedbackTemplateRows.reduce((max, row) => Math.max(max, Number(row.total || 0)), 0);
  }

  get feedbackDepartmentRows() {
    return Array.isArray(this.feedbackDashboard?.departments) ? this.feedbackDashboard.departments : [];
  }

  get feedbackDepartmentMax() {
    return this.feedbackDepartmentRows.reduce((max, row) => Math.max(max, Number(row.count || 0)), 0);
  }

  get feedbackStatusDonutStyle() {
    const summary = this.feedbackDashboardSummary;
    const total = Number(summary.total || 0);
    if (!total) {
      return 'conic-gradient(#e7eef8 0% 100%)';
    }
    const draft = Number(((Number(summary.draft || 0) / total) * 100).toFixed(2));
    const scheduled = Number(((Number(summary.scheduled || 0) / total) * 100).toFixed(2));
    const applied = Math.max(100 - draft - scheduled, 0);
    const firstStop = draft;
    const secondStop = draft + scheduled;
    return `conic-gradient(#9aa9bf 0% ${firstStop}%, #f0cb59 ${firstStop}% ${secondStop}%, #1ec2bf ${secondStop}% ${secondStop + applied}%)`;
  }

  get feedbackRecentAppliedRows() {
    return Array.isArray(this.feedbackDashboard?.recent_applied) ? this.feedbackDashboard.recent_applied : [];
  }

  get feedbackQuestionInsights() {
    return Array.isArray(this.feedbackDashboard?.question_insights) ? this.feedbackDashboard.question_insights : [];
  }

  get feedbackQuestionInsightsMessage() {
    return String(this.feedbackDashboard?.question_insights_message || '').trim();
  }

  get feedbackQuestionInsightsTemplateTitle() {
    return String(this.feedbackDashboard?.insights_template_title || '').trim();
  }

  feedbackQuestionOptionBarWidth(percent: number) {
    const value = Number(percent || 0);
    return Math.max(2, Math.min(value, 100));
  }


  get pagedEmployees() {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredEmployees.slice(start, start + this.pageSize);
  }

  get totalPages() {
    return Math.max(1, Math.ceil(this.filteredEmployees.length / this.pageSize));
  }

  get pagedDigitalAdmissionEmployees() {
    const start = (this.digitalAdmissionPage - 1) * this.pageSize;
    return this.filteredDigitalAdmissionEmployees.slice(start, start + this.pageSize);
  }

  get digitalAdmissionTotalPages() {
    return Math.max(1, Math.ceil(this.filteredDigitalAdmissionEmployees.length / this.pageSize));
  }

  get pagedHistory() {
    const start = (this.historyPage - 1) * this.historyPageSize;
    return this.history.slice(start, start + this.historyPageSize);
  }

  get historyTotalPages() {
    return Math.max(1, Math.ceil(this.history.length / this.historyPageSize));
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages) {
      return;
    }
    this.currentPage = page;
  }

  goToHistoryPage(page: number) {
    if (page < 1 || page > this.historyTotalPages) {
      return;
    }
    this.historyPage = page;
  }

  goToDigitalAdmissionPage(page: number) {
    if (page < 1 || page > this.digitalAdmissionTotalPages) {
      return;
    }
    this.digitalAdmissionPage = page;
  }

  statusLabel(value?: string | null): string {
    const normalized = String(value || '').toUpperCase();
    const labels: Record<string, string> = {
      ACTIVE: 'Ativo',
      INACTIVE: 'Inativo',
      ON_LEAVE: 'Afastado',
      TERMINATED: 'Desligado',
      IN_EXPERIENCE: 'Em experiência',
      PENDING_ADMISSION: 'Admissão pendente',
      ADMISSION_COMPLETED: 'Aguardando análise',
      ADMISSION_CANCELED: 'Admissão cancelada'
    };
    return labels[normalized] || value || '—';
  }

  admissionCompletionPercentage(employee: ControlDpEmployee): number {
    const status = String(employee?.status || '').toUpperCase();
    if (status === 'ADMISSION_COMPLETED') {
      return 100;
    }
    const checks = [
      !!employee?.photo_url,
      !!employee?.rg,
      !!employee?.birth_date,
      !!employee?.birth_location,
      !!employee?.race,
      !!employee?.marital_status,
      !!employee?.mother_name,
      !!employee?.education_level,
      String(employee?.gender || '').toUpperCase() !== 'NOT_INFORMED' && !!employee?.gender,
      !!employee?.phone,
      !!employee?.emergency_contact_name,
      !!employee?.emergency_contact_phone,
      !!employee?.cep,
      !!employee?.street,
      !!employee?.number,
      !!employee?.neighborhood,
      !!employee?.city,
      !!employee?.state
    ];
    const filled = checks.filter(Boolean).length;
    return Math.round((filled / checks.length) * 100);
  }

  isAdmissionLinkExpired(employee: ControlDpEmployee): boolean {
    if (!employee?.admission_expires_at) {
      return false;
    }
    const expiresAt = new Date(employee.admission_expires_at).getTime();
    if (Number.isNaN(expiresAt)) {
      return false;
    }
    return expiresAt < Date.now();
  }

  admissionLinkStateLabel(employee: ControlDpEmployee): string {
    if (!employee?.admission_token || !employee?.admission_expires_at) {
      return 'Sem link';
    }
    return this.isAdmissionLinkExpired(employee) ? 'Expirado' : 'Ativo';
  }

  canReactivateAdmissionLink(employee: ControlDpEmployee): boolean {
    const status = String(employee?.status || '').toUpperCase();
    if (!['PENDING_ADMISSION', 'ADMISSION_CANCELED'].includes(status)) {
      return false;
    }
    return this.isAdmissionLinkExpired(employee) && !!employee?.personal_email;
  }

  reactivateAdmissionLink(employee: ControlDpEmployee) {
    if (!this.tenantId || !employee?.id || !this.canReactivateAdmissionLink(employee)) {
      return;
    }
    this.reactivatingAdmissionEmployeeId = employee.id;
    this.service
      .reactivateAdmissionLink(employee.id, {
        tenant_id: this.tenantId,
        user_id: this.userId
      })
      .subscribe({
        next: (response) => {
          const updatedEmployee = response?.employee;
          if (updatedEmployee?.id) {
            this.employees = this.employees.map((item) => (item.id === updatedEmployee.id ? { ...item, ...updatedEmployee } : item));
          } else {
            this.loadEmployees();
          }
          this.reactivatingAdmissionEmployeeId = null;
        },
        error: () => {
          this.reactivatingAdmissionEmployeeId = null;
        }
      });
  }

  onEmployeeSearchChange() {
    this.currentPage = 1;
    this.digitalAdmissionPage = 1;
  }

  setActiveTab(tab: TabKey) {
    if (tab === 'imports' && !this.isAdmin) {
      return;
    }
    if (tab === 'digitalAdmissions' && !this.isMaster) {
      return;
    }
    this.activeTab = tab;
    this.currentPage = 1;
    this.digitalAdmissionPage = 1;
    if (tab === 'dashboard') {
      this.loadMoodThermometer(this.moodPeriodMonths);
      if (!this.feedbackTemplates.length && !this.feedbackTemplatesLoading) {
        this.loadFeedbackTemplates();
      }
      if (this.showDashboardFeedback) {
        this.loadFeedbackDashboard();
      }
    }
    try {
      localStorage.setItem(this.tabStorageKey, tab);
    } catch (_error) {
      // Ignore storage errors on restricted environments.
    }
  }

  onDashboardFilterChange() {
    this.dashboardFilterTouched = true;
    if (this.dashboardFilter !== 'FEEDBACK') {
      this.feedbackDashboardTemplateId = '';
    }
    if (this.dashboardFilter === 'FEEDBACK' && !this.feedbackTemplates.length && !this.feedbackTemplatesLoading) {
      this.loadFeedbackTemplates();
    }
    this.feedbackDashboardError = '';
    if (this.activeTab === 'dashboard') {
      this.loadMoodThermometer(this.moodPeriodMonths);
      if (this.showDashboardFeedback) {
        this.loadFeedbackDashboard();
      }
    }
  }

  onDashboardFeedbackTemplateChange() {
    if (!this.showDashboardFeedback) {
      return;
    }
    this.loadFeedbackDashboard();
  }

  onFeedbackDashboardTemplateChange(templateId: string) {
    this.feedbackDashboardTemplateId = String(templateId || '');
    this.onDashboardFeedbackTemplateChange();
  }

  setFeedbackDashboardPeriod(months: number) {
    if (this.feedbackDashboardPeriodMonths === months) {
      return;
    }
    this.feedbackDashboardPeriodMonths = months;
    this.loadFeedbackDashboard();
  }

  private resolveIsAdmin(user: any) {
    const name = String(user?.role_name || user?.role?.name || '').toLowerCase();
    return name === 'admin';
  }

  private resolveIsMaster(user: any) {
    const name = String(user?.role_name || user?.role?.name || '').toLowerCase();
    return name === 'master';
  }

  openEmployeeModal(employee?: ControlDpEmployee) {
    this.employeeModalSection = 'personal';
    this.clearEmployeePhotoPreview();
    this.employeePhotoUploading = false;
    if (employee) {
      this.selectedEmployee = employee;
      this.employeeDocsTab = 'controldocs';
      this.employeeForm = {
        ...this.defaultEmployeeForm(),
        ...employee,
        birth_date: this.toInputDate(employee.birth_date),
        hired_at: this.toInputDate(employee.hired_at),
        experience_45_days: this.toInputDate(employee.experience_45_days),
        experience_90_days: this.toInputDate(employee.experience_90_days)
      };
      this.loadEmployeeExtras(employee.id);
      if (this.employeeForm.role_id && this.isSalaryEmpty(this.employeeForm.salary)) {
        this.onRoleChange();
      }
    } else {
      this.selectedEmployee = null;
      this.employeeForm = this.defaultEmployeeForm();
      this.employeeDocsTab = 'attachments';
      this.resetEmployeeDocsState();
      this.attachments = [];
      this.occurrences = [];
      this.history = [];
    }
    this.showEmployeeModal = true;
  }

  onRoleChange() {
    if (!this.employeeForm.role_id) {
      return;
    }
    const role = this.roles.find((item) => String(item.id) === String(this.employeeForm.role_id));
    if (!role) {
      return;
    }
    if (role.base_salary !== null && role.base_salary !== undefined) {
      this.employeeForm.salary = String(role.base_salary);
    }
  }

  openOccurrenceModal(employee: ControlDpEmployee) {
    this.selectedEmployee = employee;
    this.occurrenceForm = this.defaultOccurrenceForm();
    this.clearOccurrenceAttachment();
    this.occurrenceModalSection = 'data';
    this.occurrenceHistoryLoading = true;
    this.occurrences = [];
    if (this.tenantId) {
      this.service.listOccurrences(this.tenantId, employee.id).subscribe({
        next: (rows) => {
          this.occurrences = rows || [];
          this.occurrenceHistoryLoading = false;
        },
        error: () => {
          this.occurrences = [];
          this.occurrenceHistoryLoading = false;
        }
      });
    } else {
      this.occurrences = [];
      this.occurrenceHistoryLoading = false;
    }
    this.showOccurrenceModal = true;
  }

  openHistoryModal(employee: ControlDpEmployee) {
    this.selectedEmployee = employee;
    this.historyPage = 1;
    this.loadHistory(employee.id);
    this.showHistoryModal = true;
  }

  openRoleModal() {
    this.roleForm = { name: '', base_salary: '' };
    this.editingRole = null;
    this.roleModalTab = 'cadastro';
    this.showRoleModal = true;
  }

  openFeedbackModal(initialTab: FeedbackModalTab = 'cadastro') {
    this.feedbackModalTab = initialTab;
    this.feedbackError = '';
    this.feedbackTemplatesError = '';
    this.startNewFeedbackDraft();
    this.showFeedbackModal = true;
    this.loadFeedbackTemplates();
  }

  closeFeedbackModal() {
    this.showFeedbackModal = false;
    this.feedbackModalTab = 'cadastro';
    this.feedbackError = '';
    this.feedbackSaving = false;
    this.feedbackEditorLoading = false;
    this.feedbackTemplatesError = '';
    this.feedbackEditingSurveyId = null;
    this.feedbackEditingTemplate = null;
  }

  openEmployeeFeedbackModal(employee: ControlDpEmployee) {
    if (!employee?.id) {
      return;
    }
    this.employeeFeedbackTarget = employee;
    this.employeeFeedbackTab = 'REGISTRO';
    this.employeeFeedbackError = '';
    this.employeeFeedbackQuestionsLoading = false;
    this.employeeFeedbackSaving = false;
    this.employeeFeedbackForm = this.defaultEmployeeFeedbackForm();
    this.employeeFeedbackQuestions = [];
    this.employeeFeedbackAnswers = {};
    this.employeeFeedbackRecords = [];
    this.showEmployeeFeedbackModal = true;
    this.loadFeedbackTemplates();
    this.loadEmployeeFeedbackRecords(employee.id);
  }

  closeEmployeeFeedbackModal() {
    this.showEmployeeFeedbackModal = false;
    this.employeeFeedbackTarget = null;
    this.employeeFeedbackTab = 'REGISTRO';
    this.employeeFeedbackLoading = false;
    this.employeeFeedbackQuestionsLoading = false;
    this.employeeFeedbackSaving = false;
    this.employeeFeedbackError = '';
    this.employeeFeedbackQuestions = [];
    this.employeeFeedbackAnswers = {};
    this.employeeFeedbackRecords = [];
    this.employeeFeedbackForm = this.defaultEmployeeFeedbackForm();
  }

  onEmployeeFeedbackTabClick(event: MouseEvent, tab: EmployeeFeedbackTab | string) {
    event.preventDefault();
    event.stopPropagation();
    this.setEmployeeFeedbackTab(tab);
  }

  setEmployeeFeedbackTab(tab: EmployeeFeedbackTab | string) {
    const normalized = String(tab || '').trim().toUpperCase();
    const allowedTabs: EmployeeFeedbackTab[] = ['REGISTRO', 'DRAFT', 'SCHEDULED', 'APPLIED'];
    const nextTab = (allowedTabs.includes(normalized as EmployeeFeedbackTab)
      ? (normalized as EmployeeFeedbackTab)
      : 'REGISTRO');
    if (this.employeeFeedbackTab === nextTab) {
      return;
    }
    this.employeeFeedbackTab = nextTab;
    this.employeeFeedbackError = '';
    this.cdr.detectChanges();
    if (this.employeeFeedbackTab !== 'REGISTRO' && this.employeeFeedbackTarget?.id && !this.employeeFeedbackLoading) {
      this.loadEmployeeFeedbackRecords(this.employeeFeedbackTarget.id);
    }
  }

  get filteredEmployeeFeedbackRecords() {
    if (this.employeeFeedbackTab === 'REGISTRO') {
      return [];
    }
    return this.employeeFeedbackRecords.filter((item) => String(item.status || '').toUpperCase() === this.employeeFeedbackTab);
  }

  get availableEmployeeFeedbackTemplates() {
    if (!this.employeeFeedbackTarget) {
      return this.feedbackTemplates;
    }
    const employeeSectorId = Number(this.employeeFeedbackTarget.sector_id || 0);
    const employeeDepartmentId = Number(this.employeeFeedbackTarget.department_id || 0);
    return this.feedbackTemplates.filter((template) => {
      const sectorMatches = !template.sector_id || Number(template.sector_id) === employeeSectorId;
      const departmentMatches = !template.department_id || Number(template.department_id) === employeeDepartmentId;
      return sectorMatches && departmentMatches;
    });
  }

  loadEmployeeFeedbackRecords(employeeId?: number) {
    const targetEmployeeId = Number(employeeId || this.employeeFeedbackTarget?.id || 0);
    if (!this.tenantId || !targetEmployeeId) {
      this.employeeFeedbackRecords = [];
      return;
    }
    this.employeeFeedbackLoading = true;
    this.employeeFeedbackError = '';
    this.service.listEmployeeFeedbacks(this.tenantId, targetEmployeeId).subscribe({
      next: (rows) => {
        this.employeeFeedbackRecords = rows || [];
        this.employeeFeedbackLoading = false;
      },
      error: () => {
        this.employeeFeedbackLoading = false;
        this.employeeFeedbackRecords = [];
        this.employeeFeedbackError = 'Não foi possível carregar os registros de feedback.';
      }
    });
  }

  editEmployeeFeedbackRecord(record: ControlDpEmployeeFeedback) {
    const parsedAnswers = this.parseEmployeeFeedbackAnswers(record.answers_json || null);
    this.employeeFeedbackForm = {
      feedback_id: Number(record.id || 0) || null,
      feedback_template_id: record.feedback_template_id ? String(record.feedback_template_id) : '',
      notes: String(record.notes || ''),
      collaborator_notes: String(record.collaborator_notes || ''),
      scheduled_for: this.toDateTimeLocalInput(record.scheduled_for || null)
    };
    this.employeeFeedbackTab = 'REGISTRO';
    this.employeeFeedbackError = '';
    this.loadEmployeeFeedbackQuestionsByTemplateId(Number(record.feedback_template_id || 0), parsedAnswers);
  }

  onEmployeeFeedbackTemplateChange(templateIdValue: string | number) {
    const templateId = Number(templateIdValue || 0);
    this.employeeFeedbackQuestions = [];
    this.employeeFeedbackAnswers = {};
    this.employeeFeedbackForm.feedback_id = null;
    if (!templateId) {
      this.employeeFeedbackQuestionsLoading = false;
      return;
    }
    this.loadEmployeeFeedbackQuestionsByTemplateId(templateId);
  }

  saveEmployeeFeedback(status: EmployeeFeedbackStatus) {
    if (!this.tenantId || !this.employeeFeedbackTarget?.id || this.employeeFeedbackSaving) {
      return;
    }
    const templateId = Number(this.employeeFeedbackForm.feedback_template_id);
    if (!templateId) {
      this.employeeFeedbackError = 'Selecione um template de feedback.';
      return;
    }
    if (status === 'SCHEDULED' && !String(this.employeeFeedbackForm.scheduled_for || '').trim()) {
      this.employeeFeedbackError = 'Informe a data/hora para agendar.';
      return;
    }
    if (!this.employeeFeedbackQuestions.length) {
      this.employeeFeedbackError = 'Esse template não possui perguntas disponíveis para resposta.';
      return;
    }

    const requiredError = this.validateEmployeeFeedbackRequiredAnswers();
    if (requiredError) {
      this.employeeFeedbackError = requiredError;
      return;
    }

    const answersPayload = this.serializeEmployeeFeedbackAnswers();

    this.employeeFeedbackSaving = true;
    this.employeeFeedbackError = '';
    this.service
      .saveEmployeeFeedback(this.employeeFeedbackTarget.id, {
        tenant_id: this.tenantId,
        feedback_id: this.employeeFeedbackForm.feedback_id,
        feedback_template_id: templateId,
        status,
        scheduled_for: status === 'SCHEDULED' ? this.employeeFeedbackForm.scheduled_for : null,
        notes: this.employeeFeedbackForm.notes || null,
        collaborator_notes: this.employeeFeedbackForm.collaborator_notes || null,
        answers_json: answersPayload,
        created_by: this.userId || null,
        updated_by: this.userId || null
      })
      .subscribe({
        next: () => {
          this.employeeFeedbackSaving = false;
          this.employeeFeedbackTab = status;
          this.employeeFeedbackForm = this.defaultEmployeeFeedbackForm();
          this.employeeFeedbackQuestions = [];
          this.employeeFeedbackAnswers = {};
          this.loadEmployeeFeedbackRecords(this.employeeFeedbackTarget?.id);
        },
        error: (error) => {
          this.employeeFeedbackSaving = false;
          this.employeeFeedbackError = error?.error?.error || 'Não foi possível salvar o feedback.';
        }
      });
  }

  async loadEmployeeFeedbackQuestionsByTemplateId(templateId: number, prefilledAnswers?: Record<string, any>) {
    if (!this.tenantId || !templateId) {
      this.employeeFeedbackQuestions = [];
      this.employeeFeedbackAnswers = {};
      this.employeeFeedbackQuestionsLoading = false;
      return;
    }
    const template = this.feedbackTemplates.find((item) => Number(item.id) === Number(templateId));
    const fallbackRecord = this.employeeFeedbackRecords.find((item) => Number(item.feedback_template_id) === Number(templateId));
    const surveyId = Number(template?.survey_id || fallbackRecord?.survey_id || 0);
    if (!surveyId) {
      this.employeeFeedbackQuestions = [];
      this.employeeFeedbackAnswers = {};
      this.employeeFeedbackQuestionsLoading = false;
      this.employeeFeedbackError = 'Template selecionado sem questionário vinculado.';
      return;
    }
    const requestId = ++this.employeeFeedbackQuestionRequestId;
    this.employeeFeedbackQuestionsLoading = true;
    this.employeeFeedbackError = '';

    try {
      const detail = await firstValueFrom(this.controlquestion.getSurvey(this.tenantId, surveyId));
      if (requestId !== this.employeeFeedbackQuestionRequestId) {
        return;
      }
      const questions = Array.isArray(detail?.questions) ? detail.questions : [];
      const options = Array.isArray(detail?.options) ? detail.options : [];

      const mappedQuestions: EmployeeFeedbackQuestion[] = questions
        .slice()
        .sort(
          (a: ControlQuestion, b: ControlQuestion) =>
            (Number(a.order_index || 0) - Number(b.order_index || 0)) || (Number(a.id || 0) - Number(b.id || 0))
        )
        .map((question: ControlQuestion) => {
          const questionOptions = options
            .filter((option: ControlQuestionOption) => Number(option.question_id) === Number(question.id))
            .sort(
              (a: ControlQuestionOption, b: ControlQuestionOption) =>
                (Number(a.order_index || 0) - Number(b.order_index || 0)) || (Number(a.id || 0) - Number(b.id || 0))
            )
            .map((option: ControlQuestionOption) => ({
              id: option.id ? Number(option.id) : null,
              label: String(option.label || ''),
              order_index: Number(option.order_index || 0)
            }));

          return {
            id: Number(question.id),
            title: String(question.title || ''),
            description: String(question.description || ''),
            question_type: String(question.question_type || 'TEXT').toUpperCase(),
            required: Number(question.required) === 1,
            order_index: Number(question.order_index || 0),
            options: questionOptions
          };
        });

      this.employeeFeedbackQuestions = mappedQuestions;
      this.employeeFeedbackAnswers = this.buildEmployeeFeedbackAnswersModel(mappedQuestions, prefilledAnswers);
      if (!mappedQuestions.length) {
        this.employeeFeedbackError = 'Esse template não possui perguntas disponíveis.';
      }
    } catch (_error) {
      if (requestId === this.employeeFeedbackQuestionRequestId) {
        this.employeeFeedbackQuestions = [];
        this.employeeFeedbackAnswers = {};
        this.employeeFeedbackError = 'Não foi possível carregar as perguntas do template.';
      }
    } finally {
      if (requestId === this.employeeFeedbackQuestionRequestId) {
        this.employeeFeedbackQuestionsLoading = false;
      }
    }
  }

  employeeFeedbackQuestionAnswer(questionId: number) {
    return this.employeeFeedbackAnswers[String(questionId)];
  }

  setEmployeeFeedbackQuestionAnswer(questionId: number, value: any) {
    this.employeeFeedbackAnswers[String(questionId)] = value;
  }

  employeeFeedbackOptionValue(option: EmployeeFeedbackQuestionOption) {
    const optionId = Number(option.id || 0);
    if (optionId) {
      return String(optionId);
    }
    return String(option.label || '').trim();
  }

  isEmployeeFeedbackOptionChecked(questionId: number, optionValue: string) {
    const current = this.employeeFeedbackQuestionAnswer(questionId);
    if (!Array.isArray(current)) {
      return false;
    }
    return current.map((item) => String(item)).includes(String(optionValue));
  }

  toggleEmployeeFeedbackOption(questionId: number, optionValue: string, checked: boolean) {
    const current = this.employeeFeedbackQuestionAnswer(questionId);
    const values = Array.isArray(current) ? current.map((item) => String(item)) : [];
    const normalized = String(optionValue);
    if (checked) {
      if (!values.includes(normalized)) {
        values.push(normalized);
      }
    } else {
      const nextValues = values.filter((item) => item !== normalized);
      this.employeeFeedbackAnswers[String(questionId)] = nextValues;
      return;
    }
    this.employeeFeedbackAnswers[String(questionId)] = values;
  }

  isEmployeeFeedbackChoiceQuestion(question: EmployeeFeedbackQuestion) {
    const type = String(question.question_type || '').toUpperCase();
    return ['SINGLE_CHOICE', 'DROPDOWN'].includes(type);
  }

  isEmployeeFeedbackMultipleQuestion(question: EmployeeFeedbackQuestion) {
    const type = String(question.question_type || '').toUpperCase();
    return type === 'MULTIPLE_CHOICE';
  }

  isEmployeeFeedbackNumberQuestion(question: EmployeeFeedbackQuestion) {
    return String(question.question_type || '').toUpperCase() === 'NUMBER';
  }

  isEmployeeFeedbackDateQuestion(question: EmployeeFeedbackQuestion) {
    return String(question.question_type || '').toUpperCase() === 'DATE';
  }

  isEmployeeFeedbackEmailQuestion(question: EmployeeFeedbackQuestion) {
    return String(question.question_type || '').toUpperCase() === 'EMAIL';
  }

  private validateEmployeeFeedbackRequiredAnswers() {
    for (let index = 0; index < this.employeeFeedbackQuestions.length; index += 1) {
      const question = this.employeeFeedbackQuestions[index];
      if (!question.required) {
        continue;
      }
      const normalized = this.normalizeEmployeeFeedbackAnswer(question, this.employeeFeedbackQuestionAnswer(question.id));
      if (!this.employeeFeedbackHasAnswer(normalized)) {
        return `Pergunta obrigatória sem resposta: ${question.title || `Pergunta ${index + 1}`}.`;
      }
    }
    return '';
  }

  private serializeEmployeeFeedbackAnswers() {
    const answers = this.employeeFeedbackQuestions.map((question) => ({
      question_id: question.id,
      question_type: question.question_type,
      answer: this.normalizeEmployeeFeedbackAnswer(question, this.employeeFeedbackQuestionAnswer(question.id))
    }));
    return JSON.stringify({ answers });
  }

  private normalizeEmployeeFeedbackAnswer(question: EmployeeFeedbackQuestion, value: any) {
    if (this.isEmployeeFeedbackMultipleQuestion(question)) {
      const values = Array.isArray(value) ? value.map((item) => String(item).trim()).filter(Boolean) : [];
      return Array.from(new Set(values));
    }
    if (this.isEmployeeFeedbackNumberQuestion(question)) {
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (this.isEmployeeFeedbackDateQuestion(question) || this.isEmployeeFeedbackChoiceQuestion(question) || this.isEmployeeFeedbackEmailQuestion(question)) {
      const normalized = String(value || '').trim();
      return normalized || null;
    }
    const normalizedText = String(value || '').trim();
    return normalizedText || null;
  }

  private employeeFeedbackHasAnswer(value: any) {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value);
    }
    return !!String(value || '').trim();
  }

  private parseEmployeeFeedbackAnswers(raw?: string | null): Record<string, any> {
    if (!raw) {
      return {};
    }
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (Array.isArray(parsed)) {
        return parsed.reduce((acc: Record<string, any>, item: any) => {
          const questionId = Number(item?.question_id || 0);
          if (questionId) {
            acc[String(questionId)] = item?.answer ?? null;
          }
          return acc;
        }, {});
      }
      if (Array.isArray(parsed?.answers)) {
        return parsed.answers.reduce((acc: Record<string, any>, item: any) => {
          const questionId = Number(item?.question_id || 0);
          if (questionId) {
            acc[String(questionId)] = item?.answer ?? null;
          }
          return acc;
        }, {});
      }
      if (parsed && typeof parsed === 'object') {
        const entries = Object.entries(parsed);
        const result: Record<string, any> = {};
        entries.forEach(([key, value]) => {
          if (/^\d+$/.test(String(key))) {
            result[String(key)] = value;
          }
        });
        return result;
      }
      return {};
    } catch (_error) {
      return {};
    }
  }

  private buildEmployeeFeedbackAnswersModel(
    questions: EmployeeFeedbackQuestion[],
    prefilledAnswers?: Record<string, any>
  ): Record<string, any> {
    const source = prefilledAnswers || {};
    const nextAnswers: Record<string, any> = {};
    questions.forEach((question) => {
      const key = String(question.id);
      const value = source[key];
      if (this.isEmployeeFeedbackMultipleQuestion(question)) {
        nextAnswers[key] = Array.isArray(value) ? value.map((item) => String(item)) : [];
        return;
      }
      if (value === undefined || value === null) {
        nextAnswers[key] = '';
        return;
      }
      nextAnswers[key] = value;
    });
    return nextAnswers;
  }

  setFeedbackModalTab(tab: FeedbackModalTab) {
    this.feedbackModalTab = tab;
    this.feedbackError = '';
    this.feedbackTemplatesError = '';
    if (tab === 'feedbacks') {
      this.loadFeedbackTemplates();
    }
  }

  startNewFeedbackDraft() {
    this.feedbackEditingSurveyId = null;
    this.feedbackEditingTemplate = null;
    this.feedbackForm = this.defaultFeedbackForm();
    this.feedbackQuestions = [this.buildFeedbackQuestionDraft()];
  }

  loadFeedbackTemplates() {
    if (!this.tenantId) {
      this.feedbackTemplates = [];
      return;
    }
    this.feedbackTemplatesLoading = true;
    this.feedbackTemplatesError = '';
    this.service.listFeedbackTemplates(this.tenantId).subscribe({
      next: (rows) => {
        this.feedbackTemplates = rows || [];
        this.feedbackTemplatesLoading = false;
      },
      error: () => {
        this.feedbackTemplates = [];
        this.feedbackTemplatesLoading = false;
        this.feedbackTemplatesError = 'Não foi possível carregar os feedbacks.';
      }
    });
  }

  async editFeedbackTemplate(template: ControlDpFeedbackTemplate) {
    if (!this.tenantId || !template?.survey_id) {
      return;
    }
    this.feedbackEditorLoading = true;
    this.feedbackError = '';
    this.feedbackTemplatesError = '';
    try {
      const detail = await firstValueFrom(this.controlquestion.getSurvey(this.tenantId, Number(template.survey_id)));
      const survey = detail?.survey;
      const questions = Array.isArray(detail?.questions) ? detail.questions : [];
      const questionIds = new Set(questions.map((question: any) => Number(question.id)).filter((id: number) => !!id));
      const tenantOptions = Array.isArray(detail?.options) ? detail.options : [];
      const options = tenantOptions.filter((option: any) => questionIds.has(Number(option.question_id)));

      const mappedQuestions: FeedbackQuestionDraft[] = questions
        .slice()
        .sort((a: any, b: any) => (Number(a.order_index || 0) - Number(b.order_index || 0)) || (Number(a.id || 0) - Number(b.id || 0)))
        .map((question: any) => {
          const type = String(question.question_type || 'SINGLE_CHOICE').toUpperCase() as FeedbackQuestionType;
          const optionItems = options
            .filter((option: any) => Number(option.question_id) === Number(question.id))
            .sort((a: any, b: any) => (Number(a.order_index || 0) - Number(b.order_index || 0)) || (Number(a.id || 0) - Number(b.id || 0)))
            .map((option: any) => ({ id: Number(option.id), label: String(option.label || '') }));

          return {
            id: Number(question.id),
            title: String(question.title || ''),
            description: String(question.description || ''),
            question_type: type,
            required: Number(question.required) === 1 || question.required === true,
            options: this.feedbackQuestionNeedsOptions(type) ? optionItems : []
          };
        });

      this.feedbackEditingTemplate = template;
      this.feedbackEditingSurveyId = Number(template.survey_id);
      const sectorIds = this.parseNumericIdArray((template as any)?.sector_ids_json);
      const departmentIds = this.parseNumericIdArray((template as any)?.department_ids_json);
      const normalizedSectorIds = sectorIds.length
        ? sectorIds
        : (template.sector_id ? [Number(template.sector_id)] : []);
      const normalizedDepartmentIds = departmentIds.length
        ? departmentIds
        : (template.department_id ? [Number(template.department_id)] : []);

      this.feedbackForm = {
        title: String(survey?.title || template.title || ''),
        description: String(survey?.description || template.description || ''),
        intro_text: String(survey?.intro_text || ''),
        sector_ids: normalizedSectorIds.map((id) => String(id)),
        department_ids: normalizedDepartmentIds.map((id) => String(id))
      };
      this.feedbackQuestions = mappedQuestions.length ? mappedQuestions : [this.buildFeedbackQuestionDraft()];
      this.feedbackModalTab = 'cadastro';
    } catch (_error) {
      this.feedbackError = 'Não foi possível carregar o feedback para edição.';
    } finally {
      this.feedbackEditorLoading = false;
    }
  }

  get feedbackAvailableDepartments() {
    const sectorIds = Array.isArray(this.feedbackForm.sector_ids)
      ? this.feedbackForm.sector_ids.map((id: any) => String(id))
      : [];
    if (!sectorIds.length) {
      return this.departments;
    }
    return this.departments.filter(
      (dept) => sectorIds.includes(String(dept.sector_id))
    );
  }

  onFeedbackSectorChange() {
    const allowedDepartmentIds = new Set(
      this.feedbackAvailableDepartments.map((dept) => String(dept.id))
    );
    const selectedDepartmentIds = Array.isArray(this.feedbackForm.department_ids)
      ? this.feedbackForm.department_ids.map((id: any) => String(id))
      : [];
    this.feedbackForm.department_ids = selectedDepartmentIds.filter((id: string) => allowedDepartmentIds.has(id));
  }

  onFeedbackSectorMultiChange(values: any) {
    const selectedValues = Array.isArray(values)
      ? values.map((item: any) => String(item))
      : [];
    const allSectorIds = this.sectors.map((sector) => String(sector.id));
    const requestedSelectAll = selectedValues.includes(this.feedbackSelectAllSectorsValue);

    if (requestedSelectAll) {
      const alreadyAllSelected = allSectorIds.length > 0 && allSectorIds.every((id) => selectedValues.includes(id));
      this.feedbackForm.sector_ids = alreadyAllSelected ? [] : [...allSectorIds];
    } else {
      this.feedbackForm.sector_ids = selectedValues.filter((id) => id !== this.feedbackSelectAllSectorsValue);
    }

    this.onFeedbackSectorChange();
    this.feedbackForm.department_ids = (Array.isArray(this.feedbackForm.department_ids) ? this.feedbackForm.department_ids : [])
      .map((item: any) => String(item))
      .filter((id: string) => id !== this.feedbackSelectAllDepartmentsValue);
  }

  onFeedbackDepartmentMultiChange(values: any) {
    const selectedValues = Array.isArray(values)
      ? values.map((item: any) => String(item))
      : [];
    const allowedDepartmentIds = this.feedbackAvailableDepartments.map((dept) => String(dept.id));
    const allowedSet = new Set(allowedDepartmentIds);
    const requestedSelectAll = selectedValues.includes(this.feedbackSelectAllDepartmentsValue);

    if (requestedSelectAll) {
      const alreadyAllSelected = allowedDepartmentIds.length > 0 && allowedDepartmentIds.every((id) => selectedValues.includes(id));
      this.feedbackForm.department_ids = alreadyAllSelected ? [] : [...allowedDepartmentIds];
      return;
    }

    this.feedbackForm.department_ids = selectedValues
      .filter((id) => id !== this.feedbackSelectAllDepartmentsValue)
      .filter((id) => allowedSet.has(id));
  }

  addFeedbackQuestion(type: FeedbackQuestionType = 'SINGLE_CHOICE') {
    this.feedbackQuestions.push(this.buildFeedbackQuestionDraft(type));
  }

  removeFeedbackQuestion(index: number) {
    this.feedbackQuestions.splice(index, 1);
  }

  onFeedbackQuestionTypeChange(question: FeedbackQuestionDraft) {
    if (this.feedbackQuestionNeedsOptions(question.question_type)) {
      if (!question.options.length) {
        question.options = [this.buildFeedbackOptionDraft('Opção 1'), this.buildFeedbackOptionDraft('Opção 2')];
      }
      return;
    }
    question.options = [];
  }

  addFeedbackOption(question: FeedbackQuestionDraft) {
    question.options.push(this.buildFeedbackOptionDraft(`Opção ${question.options.length + 1}`));
  }

  removeFeedbackOption(question: FeedbackQuestionDraft, optionIndex: number) {
    question.options.splice(optionIndex, 1);
  }

  feedbackQuestionNeedsOptions(type: FeedbackQuestionType) {
    return type === 'SINGLE_CHOICE' || type === 'MULTIPLE_CHOICE' || type === 'DROPDOWN';
  }

  openDigitalAdmissionModal() {
    this.digitalAdmissionForm = this.defaultDigitalAdmissionForm();
    this.clearDigitalAdmissionFeedbackTimeout();
    this.digitalAdmissionFeedbackState = 'idle';
    this.digitalAdmissionFeedbackMessage = '';
    this.resetDigitalAdmissionValidationState();
    this.showDigitalAdmissionModal = true;
  }

  openAlertModal() {
    if (!this.isMaster) {
      return;
    }
    this.clearAlertRuleMapRefreshTimeout();
    this.editingAlertRuleId = null;
    this.alertRuleForm = this.defaultAlertRuleForm();
    this.alertRuleSubmitAttempted = false;
    this.alertRuleSaving = false;
    this.alertRuleTogglingId = null;
    this.alertRuleError = '';
    this.alertRuleMapUrl = '';
    this.alertRuleMapEmbedUrl = null;
    this.alertRuleMapLoading = false;
    this.showAlertModal = true;
  }

  closeAlertModal() {
    if (this.alertRuleSaving) {
      return;
    }
    this.clearAlertRuleMapRefreshTimeout();
    this.editingAlertRuleId = null;
    this.alertRuleMapUrl = '';
    this.alertRuleMapEmbedUrl = null;
    this.alertRuleMapLoading = false;
    this.showAlertModal = false;
  }

  editAlertRule(rule: ControlDpAlertRule) {
    if (!rule || this.alertRuleSaving) {
      return;
    }
    this.showAlertModal = true;
    this.editingAlertRuleId = Number(rule.id) || null;
    this.alertRuleSubmitAttempted = false;
    this.alertRuleError = '';
    this.alertRuleForm = {
      alert_name: String(rule.alert_name || ''),
      location_type: (String(rule.location_type || 'CEP').toUpperCase() === 'ADDRESS' ? 'ADDRESS' : 'CEP') as AlertLocationType,
      location_value: this.formatAlertRuleLocationValueForUi(rule.location_type, rule.location_value),
      validation_unit: (String(rule.validation_unit || 'RADIUS_KM').toUpperCase() === 'TRAVEL_TIME'
        ? 'TRAVEL_TIME'
        : 'RADIUS_KM') as AlertValidationUnit,
      travel_mode: String(rule.travel_mode || ''),
      condition_operator: String(rule.condition_operator || 'GREATER_THAN'),
      condition_value_min: rule.condition_value_min ?? '',
      condition_value_max: rule.condition_value_max ?? '',
      action_type: 'GENERATE_ALERT',
      active: Number(rule.active) === 0 ? 0 : 1
    };
    this.onAlertValidationUnitChange();
    this.onAlertConditionOperatorChange();
    this.refreshAlertRuleMapPreview(true);
  }

  onAlertLocationTypeChange() {
    if (this.alertRuleForm.location_type !== 'CEP') {
      this.clearAlertRuleMapRefreshTimeout();
      this.alertRuleMapUrl = '';
      this.alertRuleMapEmbedUrl = null;
      this.alertRuleMapLoading = false;
      return;
    }
    this.alertRuleForm.location_value = this.formatCepMask(String(this.alertRuleForm.location_value || ''));
    this.refreshAlertRuleMapPreview();
  }

  onAlertLocationInput(event: Event) {
    if (this.alertRuleForm.location_type !== 'CEP') {
      return;
    }
    const input = event.target as HTMLInputElement | null;
    const masked = this.formatCepMask(input?.value ?? this.alertRuleForm.location_value);
    this.alertRuleForm.location_value = masked;
    if (input && input.value !== masked) {
      input.value = masked;
    }
    this.refreshAlertRuleMapPreview();
  }

  onAlertLocationBlur() {
    if (this.alertRuleForm.location_type !== 'CEP') {
      return;
    }
    this.refreshAlertRuleMapPreview(true);
  }

  onAlertRuleMapImageLoaded() {
    this.alertRuleMapLoading = false;
  }

  onAlertRuleMapImageError() {
    this.alertRuleMapLoading = false;
    this.alertRuleMapUrl = '';
    this.alertRuleMapEmbedUrl = null;
  }

  onAlertValidationUnitChange() {
    if (this.alertRuleForm.validation_unit !== 'TRAVEL_TIME') {
      this.alertRuleForm.travel_mode = '';
    }
  }

  onAlertConditionOperatorChange() {
    if (this.alertRuleForm.condition_operator !== 'BETWEEN') {
      this.alertRuleForm.condition_value_max = '';
    }
  }

  isAlertRuleFormValid() {
    const name = String(this.alertRuleForm.alert_name || '').trim();
    const locationValue = this.normalizeAlertLocationValueForApi(
      this.alertRuleForm.location_type as AlertLocationType,
      this.alertRuleForm.location_value
    );
    const min = Number(this.alertRuleForm.condition_value_min);
    const max = Number(this.alertRuleForm.condition_value_max);

    if (!name || !locationValue) {
      return false;
    }
    if (this.alertRuleForm.location_type === 'CEP' && locationValue.length !== 8) {
      return false;
    }
    if (this.alertRuleForm.validation_unit === 'TRAVEL_TIME' && !this.alertRuleForm.travel_mode) {
      return false;
    }
    if (!Number.isFinite(min) || min < 0) {
      return false;
    }
    if (this.alertRuleForm.condition_operator === 'BETWEEN') {
      if (!Number.isFinite(max) || max < min) {
        return false;
      }
    }
    return true;
  }

  isAlertCepInvalid() {
    if (this.alertRuleForm.location_type !== 'CEP') {
      return false;
    }
    const locationText = String(this.alertRuleForm.location_value || '').trim();
    if (!locationText) {
      return false;
    }
    return this.normalizeCepDigits(locationText).length !== 8;
  }

  saveAlertRule() {
    if (!this.tenantId || !this.isMaster || this.alertRuleSaving) {
      return;
    }
    this.alertRuleSubmitAttempted = true;
    this.alertRuleError = '';
    if (!this.isAlertRuleFormValid()) {
      this.alertRuleError = 'Preencha os campos obrigatórios do alerta.';
      return;
    }

    const payload = {
      tenant_id: this.tenantId,
      user_id: this.userId || null,
      alert_name: String(this.alertRuleForm.alert_name || '').trim(),
      location_type: this.alertRuleForm.location_type as AlertLocationType,
      location_value: this.normalizeAlertLocationValueForApi(
        this.alertRuleForm.location_type as AlertLocationType,
        this.alertRuleForm.location_value
      ),
      validation_unit: this.alertRuleForm.validation_unit as AlertValidationUnit,
      travel_mode: this.alertRuleForm.validation_unit === 'TRAVEL_TIME'
        ? (this.alertRuleForm.travel_mode as AlertTravelMode)
        : null,
      condition_operator: this.alertRuleForm.condition_operator as AlertConditionOperator,
      condition_value_min: Number(this.alertRuleForm.condition_value_min),
      condition_value_max: this.alertRuleForm.condition_operator === 'BETWEEN'
        ? Number(this.alertRuleForm.condition_value_max)
        : null,
      action_type: 'GENERATE_ALERT' as const,
      active: (Number(this.alertRuleForm.active) === 0 ? 0 : 1) as 0 | 1
    };

    this.alertRuleSaving = true;
    const request$ = this.editingAlertRuleId
      ? this.service.updateAlertRule(this.editingAlertRuleId, payload)
      : this.service.createAlertRule(payload);
    request$.subscribe({
      next: (savedRule) => {
        if (this.editingAlertRuleId) {
          this.alertRules = this.alertRules.map((rule) => (rule.id === savedRule.id ? savedRule : rule));
        } else {
          this.alertRules = [savedRule, ...this.alertRules];
        }
        this.alertRuleSaving = false;
        this.editingAlertRuleId = null;
        this.showAlertModal = false;
      },
      error: (error) => {
        this.alertRuleSaving = false;
        this.alertRuleError = error?.error?.error || 'Não foi possível salvar o alerta.';
      }
    });
  }

  isAlertRuleActive(rule: ControlDpAlertRule) {
    return Number(rule?.active) === 1;
  }

  toggleAlertRuleActive(rule: ControlDpAlertRule, nextChecked: boolean) {
    const ruleId = Number(rule?.id);
    if (!this.tenantId || !this.isMaster || !ruleId || this.alertRuleSaving || this.alertRuleTogglingId) {
      return;
    }
    const nextActive = (nextChecked ? 1 : 0) as 0 | 1;
    const currentActive = (Number(rule.active) === 1 ? 1 : 0) as 0 | 1;
    if (nextActive === currentActive) {
      return;
    }

    this.alertRuleError = '';
    this.alertRuleTogglingId = ruleId;

    this.service.updateAlertRule(ruleId, {
      tenant_id: this.tenantId,
      user_id: this.userId || null,
      alert_name: String(rule.alert_name || '').trim(),
      location_type: String(rule.location_type || 'CEP').toUpperCase() === 'ADDRESS' ? 'ADDRESS' : 'CEP',
      location_value: this.normalizeAlertLocationValueForApi(
        String(rule.location_type || 'CEP').toUpperCase() === 'ADDRESS' ? 'ADDRESS' : 'CEP',
        String(rule.location_value || '')
      ),
      validation_unit: String(rule.validation_unit || 'RADIUS_KM').toUpperCase() === 'TRAVEL_TIME' ? 'TRAVEL_TIME' : 'RADIUS_KM',
      travel_mode: String(rule.validation_unit || '').toUpperCase() === 'TRAVEL_TIME'
        ? ((String(rule.travel_mode || '').toUpperCase() || 'DRIVING') as AlertTravelMode)
        : null,
      condition_operator: (String(rule.condition_operator || 'GREATER_THAN').toUpperCase() as AlertConditionOperator),
      condition_value_min: Number(rule.condition_value_min ?? 0),
      condition_value_max: String(rule.condition_operator || '').toUpperCase() === 'BETWEEN'
        ? Number(rule.condition_value_max ?? 0)
        : null,
      action_type: 'GENERATE_ALERT',
      active: nextActive
    }).subscribe({
      next: (savedRule) => {
        this.alertRules = this.alertRules.map((item) => (item.id === savedRule.id ? savedRule : item));
        if (this.editingAlertRuleId && this.editingAlertRuleId === savedRule.id) {
          this.alertRuleForm.active = Number(savedRule.active) === 0 ? 0 : 1;
        }
        this.alertRuleTogglingId = null;
      },
      error: (error) => {
        this.alertRuleTogglingId = null;
        this.alertRuleError = error?.error?.error || 'Não foi possível alterar o status da regra.';
      }
    });
  }

  alertRuleLocationLabel(rule: ControlDpAlertRule) {
    const isCep = String(rule?.location_type || '').toUpperCase() === 'CEP';
    const label = isCep ? 'CEP' : 'Endereço';
    return `${label}: ${this.formatAlertRuleLocationValueForUi(rule?.location_type, rule?.location_value) || '-'}`;
  }

  alertRuleValidationLabel(rule: ControlDpAlertRule) {
    const validationUnit = String(rule?.validation_unit || '').toUpperCase();
    const base = validationUnit === 'TRAVEL_TIME' ? 'Tempo de deslocamento (min)' : 'Raio (km)';
    if (validationUnit === 'TRAVEL_TIME') {
      const mode = String(rule?.travel_mode || '').toUpperCase();
      const modeLabel = this.optionLabel(this.alertTravelModeOptions, mode);
      return modeLabel ? `${base} · ${modeLabel}` : base;
    }
    return base;
  }

  alertRuleConditionLabel(rule: ControlDpAlertRule) {
    const operatorLabel = this.optionLabel(this.alertConditionOptions, String(rule?.condition_operator || '').toUpperCase());
    const min = this.formatAlertRuleNumeric(rule?.condition_value_min);
    if (String(rule?.condition_operator || '').toUpperCase() === 'BETWEEN') {
      return `${operatorLabel || 'Condição'} ${min} e ${this.formatAlertRuleNumeric(rule?.condition_value_max)}`;
    }
    return `${operatorLabel || 'Condição'} ${min}`;
  }

  alertRuleActionLabel(rule: ControlDpAlertRule) {
    return String(rule?.action_type || '').toUpperCase() === 'GENERATE_ALERT' ? 'Ação: Gerar alerta' : 'Ação: Não informada';
  }

  alertRuleMetaLabel(rule: ControlDpAlertRule) {
    const by = String(rule?.created_by_name || '').trim();
    const when = this.toPtBrDateTime(rule?.updated_at || rule?.created_at || null);
    if (by && when) {
      return `Por ${by} · ${when}`;
    }
    if (by) {
      return `Por ${by}`;
    }
    return when || '';
  }

  get alertRuleSubmitLabel() {
    if (this.alertRuleSaving) {
      return 'Salvando...';
    }
    return this.editingAlertRuleId ? 'Salvar alterações' : 'Salvar alerta';
  }

  prefillDigitalAdmission() {
    this.digitalAdmissionForm = {
      cpf: this.formatCpfMask('39116367847'),
      full_name: 'marcelo piezentini',
      birth_date: this.toInputDate('1990-01-18'),
      hired_at: this.toInputDate('2026-02-15'),
      personal_email: 'm.piezentini@gmail.com'
    };
    this.resetDigitalAdmissionValidationState();
  }

  onDigitalAdmissionCpfInput(value: string) {
    this.digitalAdmissionForm.cpf = this.formatCpfMask(value);
  }

  onDigitalAdmissionDateBlur(field: 'birth_date' | 'hired_at', event?: FocusEvent) {
    const input = event?.target as HTMLInputElement | null;
    const sourceValue = input?.value ?? this.digitalAdmissionForm?.[field];
    const normalized = this.normalizeDateInputValue(sourceValue);
    if (normalized) {
      this.digitalAdmissionForm[field] = normalized;
      if (input) {
        input.value = this.formatDateToBrInput(normalized);
      }
      return;
    }
    if (this.digitalAdmissionForm?.[field]) {
      this.digitalAdmissionForm[field] = '';
    }
    if (input) {
      input.value = '';
    }
  }

  onDigitalAdmissionDateInput(event: Event) {
    this.applyDateMaskToInput(event);
  }

  onDigitalAdmissionDateChange(field: 'birth_date' | 'hired_at', event: MatDatepickerInputEvent<Date>) {
    const normalized = this.normalizeDateInputValue(event?.value);
    this.digitalAdmissionForm[field] = normalized || '';
  }

  onEmployeeDateInput(event: Event) {
    this.applyDateMaskToInput(event);
  }

  onEmployeeDateBlur(field: EmployeeDateFieldKey, event?: FocusEvent) {
    const input = event?.target as HTMLInputElement | null;
    const sourceValue = input?.value ?? this.employeeForm?.[field];
    const normalized = this.normalizeDateInputValue(sourceValue);
    this.employeeForm[field] = normalized || '';
    if (input) {
      input.value = normalized ? this.formatDateToBrInput(normalized) : '';
    }
  }

  onEmployeeDateChange(field: EmployeeDateFieldKey, event: MatDatepickerInputEvent<Date>) {
    const normalized = this.normalizeDateInputValue(event?.value);
    this.employeeForm[field] = normalized || '';
  }

  onOccurrenceDateInput(event: Event) {
    this.applyDateMaskToInput(event);
  }

  onOccurrenceDateBlur(field: OccurrenceDateFieldKey, event?: FocusEvent) {
    const input = event?.target as HTMLInputElement | null;
    const sourceValue = input?.value ?? this.occurrenceForm?.[field];
    const normalized = this.normalizeDateInputValue(sourceValue);
    this.occurrenceForm[field] = normalized || '';
    if (input) {
      input.value = normalized ? this.formatDateToBrInput(normalized) : '';
    }
  }

  onOccurrenceDateChange(field: OccurrenceDateFieldKey, event: MatDatepickerInputEvent<Date>) {
    const normalized = this.normalizeDateInputValue(event?.value);
    this.occurrenceForm[field] = normalized || '';
  }

  onEmployeeModalClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.employee-date-field')) {
      return;
    }
    this.employeeBirthDatePicker?.close();
    this.employeeHiredDatePicker?.close();
    this.employeeExperience45DatePicker?.close();
    this.employeeExperience90DatePicker?.close();
  }

  onOccurrenceModalClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.occurrence-date-field')) {
      return;
    }
    this.occurrenceStartDatePicker?.close();
    this.occurrenceEndDatePicker?.close();
  }

  onDigitalAdmissionModalClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.digital-date-field')) {
      return;
    }
    this.birthDatePicker?.close();
    this.hiredAtDatePicker?.close();
  }

  @HostListener('document:click', ['$event'])
  onDocumentMouseDown(event: MouseEvent) {
    const isQuickActionsMenuOpen = !!this.novoMenuTrigger?.menuOpen;
    if (
      !isQuickActionsMenuOpen
      && !this.showDigitalAdmissionModal
      && !this.showEmployeeModal
      && !this.showOccurrenceModal
      && !this.showFilterModal
      && !this.showRoleModal
      && !this.showImportModal
      && !this.showAlertModal
      && !this.showFeedbackModal
      && !this.showEmployeeFeedbackModal
    ) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    if (isQuickActionsMenuOpen) {
      const clickedInsideQuickActions = !!(
        target.closest('.quick-actions-btn')
        || target.closest('.novo-menu-panel')
        || target.closest('.mat-mdc-menu-panel')
        || target.closest('.mat-mdc-menu-item')
      );
      if (!clickedInsideQuickActions) {
        this.novoMenuTrigger?.closeMenu();
      }
    }

    const clickedInsideDateField = !!(
      target.closest('.digital-date-field')
      || target.closest('.employee-date-field')
      || target.closest('.occurrence-date-field')
      || target.closest('.mat-mdc-datepicker-content')
      || target.closest('.mat-datepicker-content')
      || target.closest('.mat-datepicker-toggle')
      || target.closest('.mat-calendar')
      || target.closest('.mat-calendar-body')
      || target.closest('.mat-calendar-body-cell')
    );

    if (!clickedInsideDateField) {
      this.closeAllDatepickers();
    }

    const clickedInsideSelect = !!(
      target.closest('.mat-mdc-select')
      || target.closest('.mat-mdc-form-field-type-mat-select')
      || target.closest('mat-select')
      || target.closest('.mat-mdc-select-trigger')
      || target.closest('.mat-mdc-select-panel')
      || target.closest('.mat-mdc-option')
      || target.closest('.mdc-list-item')
    );

    if (!clickedInsideSelect) {
      this.closeAllSelects();
    }
  }

  markDigitalAdmissionFieldTouched(field: DigitalAdmissionFieldKey) {
    this.digitalAdmissionTouched[field] = true;
  }

  get digitalAdmissionMinHiredDateValue() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  shouldShowDigitalAdmissionFieldError(field: DigitalAdmissionFieldKey) {
    return this.digitalAdmissionSubmitAttempted || !!this.digitalAdmissionTouched[field];
  }

  isDigitalAdmissionCpfValid() {
    const digits = this.normalizeCpfDigits(this.digitalAdmissionForm?.cpf);
    if (digits.length !== 11) {
      return false;
    }
    if (/^(\d)\1{10}$/.test(digits)) {
      return false;
    }
    const calcDigit = (base: string, factor: number) => {
      let total = 0;
      for (const digit of base) {
        total += Number(digit) * factor;
        factor -= 1;
      }
      const remainder = total % 11;
      return remainder < 2 ? 0 : 11 - remainder;
    };
    const first = calcDigit(digits.slice(0, 9), 10);
    const second = calcDigit(digits.slice(0, 10), 11);
    return digits.endsWith(`${first}${second}`);
  }

  isDigitalAdmissionEmailValid() {
    const email = String(this.digitalAdmissionForm?.personal_email || '').trim();
    if (!email) {
      return false;
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email);
  }

  isDigitalAdmissionHiredDateValid() {
    const hiredAt = this.parseInputDate(this.digitalAdmissionForm?.hired_at);
    if (!hiredAt) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return hiredAt.getTime() >= today.getTime();
  }

  isDigitalAdmissionFullNameValid() {
    return String(this.digitalAdmissionForm?.full_name || '').trim().length >= 3;
  }

  isDigitalAdmissionFormValid() {
    return (
      this.isDigitalAdmissionFullNameValid()
      && this.isDigitalAdmissionCpfValid()
      && this.isDigitalAdmissionHiredDateValid()
      && this.isDigitalAdmissionEmailValid()
    );
  }

  closeDigitalAdmissionModal() {
    if (this.digitalAdmissionFeedbackState === 'loading') {
      return;
    }
    this.clearDigitalAdmissionFeedbackTimeout();
    this.digitalAdmissionFeedbackState = 'idle';
    this.digitalAdmissionFeedbackMessage = '';
    this.showDigitalAdmissionModal = false;
  }

  saveDigitalAdmission() {
    if (!this.tenantId || this.digitalAdmissionFeedbackState === 'loading') {
      return;
    }
    this.digitalAdmissionSubmitAttempted = true;
    if (!this.isDigitalAdmissionFormValid()) {
      this.setDigitalAdmissionAlert('Revise os campos obrigatórios antes de enviar.', 'error');
      return;
    }
    const normalizedCpf = this.normalizeCpfDigits(this.digitalAdmissionForm.cpf);
    const payload = {
      ...this.digitalAdmissionForm,
      cpf: normalizedCpf,
      full_name: String(this.digitalAdmissionForm.full_name || '').trim(),
      birth_date: this.normalizeDateValueForApi(this.digitalAdmissionForm.birth_date),
      hired_at: this.normalizeDateValueForApi(this.digitalAdmissionForm.hired_at),
      personal_email: String(this.digitalAdmissionForm.personal_email || '').trim().toLowerCase(),
      tenant_id: this.tenantId,
      created_by: this.userId
    };
    this.digitalAdmissionFeedbackMessage = '';
    this.clearDigitalAdmissionFeedbackTimeout();
    this.digitalAdmissionFeedbackState = 'loading';
    this.service.createDigitalAdmission(payload).subscribe({
      next: (admission) => {
        this.digitalAdmissions = [admission, ...this.digitalAdmissions];
        this.digitalAdmissionFeedbackState = 'success';
        this.setDigitalAdmissionAlert('E-mail enviado com sucesso!', 'success');
        this.digitalAdmissionFeedbackTimeout = setTimeout(() => {
          this.digitalAdmissionFeedbackState = 'idle';
          this.showDigitalAdmissionModal = false;
        }, 1800);
      },
      error: (error) => {
        this.digitalAdmissionFeedbackState = 'error';
        const serverMessage = String(error?.error?.error || '').trim();
        this.digitalAdmissionFeedbackMessage = serverMessage || 'Não foi possível enviar o link. Tente novamente.';
        this.setDigitalAdmissionAlert(this.digitalAdmissionFeedbackMessage, 'error');
      }
    });
  }

  dismissDigitalAdmissionFeedback() {
    if (this.digitalAdmissionFeedbackState === 'loading') {
      return;
    }
    this.clearDigitalAdmissionFeedbackTimeout();
    this.digitalAdmissionFeedbackState = 'idle';
    this.digitalAdmissionFeedbackMessage = '';
  }

  setDigitalAdmissionAlert(text: string, variant: 'success' | 'error') {
    this.digitalAdmissionAlert.text = text;
    this.digitalAdmissionAlert.variant = variant;
    clearTimeout(this.digitalAdmissionAlertTimeout);
    this.digitalAdmissionAlertTimeout = setTimeout(() => {
      this.digitalAdmissionAlert.text = '';
    }, 4500);
  }

  private clearDigitalAdmissionFeedbackTimeout() {
    clearTimeout(this.digitalAdmissionFeedbackTimeout);
    this.digitalAdmissionFeedbackTimeout = null;
  }

  private normalizeCpfDigits(value: string) {
    return String(value || '').replace(/\D/g, '').slice(0, 11);
  }

  private formatCpfMask(value: string) {
    const digits = this.normalizeCpfDigits(value);
    if (digits.length <= 3) {
      return digits;
    }
    if (digits.length <= 6) {
      return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    }
    if (digits.length <= 9) {
      return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    }
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  }

  private normalizeCepDigits(value: string) {
    return String(value || '').replace(/\D/g, '').slice(0, 8);
  }

  private formatCepMask(value: string) {
    const digits = this.normalizeCepDigits(value);
    if (digits.length <= 5) {
      return digits;
    }
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  }

  private formatAlertRuleLocationValueForUi(locationType: any, locationValue: any) {
    if (String(locationType || '').toUpperCase() !== 'CEP') {
      return String(locationValue || '').trim();
    }
    return this.formatCepMask(String(locationValue || ''));
  }

  private normalizeAlertLocationValueForApi(locationType: AlertLocationType, locationValue: any) {
    if (String(locationType || '').toUpperCase() !== 'CEP') {
      return String(locationValue || '').trim();
    }
    return this.normalizeCepDigits(String(locationValue || ''));
  }

  private formatAlertRuleNumeric(value: any) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return '-';
    }
    return numeric.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  private optionLabel(options: Array<{ value: string; label: string }>, value: string) {
    const normalized = String(value || '').toUpperCase();
    const option = options.find((item) => String(item.value || '').toUpperCase() === normalized);
    return option?.label || '';
  }

  private parseInputDate(value: any) {
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return null;
      }
      const parsedDate = new Date(value);
      parsedDate.setHours(0, 0, 0, 0);
      return parsedDate;
    }
    const text = String(value || '').trim();
    if (!text) {
      return null;
    }
    const normalized = this.normalizeDateInputValue(text);
    if (normalized) {
      return normalized;
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);
      const date = new Date(year, month - 1, day);
      if (
        date.getFullYear() !== year
        || date.getMonth() !== month - 1
        || date.getDate() !== day
      ) {
        return null;
      }
      date.setHours(0, 0, 0, 0);
      return date;
    }
    const fallbackDate = new Date(text);
    if (Number.isNaN(fallbackDate.getTime())) {
      return null;
    }
    fallbackDate.setHours(0, 0, 0, 0);
    return fallbackDate;
  }

  private formatDateToInput(value: Date) {
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatDateToBrInput(value: Date) {
    const day = `${value.getDate()}`.padStart(2, '0');
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const year = value.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private applyDateMaskToInput(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const raw = input?.value ?? '';
    const digits = String(raw).replace(/\D/g, '').slice(0, 8);
    let masked = digits;
    if (digits.length > 4) {
      masked = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    } else if (digits.length > 2) {
      masked = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    if (input && input.value !== masked) {
      input.value = masked;
    }
  }

  private closeAllDatepickers() {
    this.birthDatePicker?.close();
    this.hiredAtDatePicker?.close();
    this.employeeBirthDatePicker?.close();
    this.employeeHiredDatePicker?.close();
    this.employeeExperience45DatePicker?.close();
    this.employeeExperience90DatePicker?.close();
    this.occurrenceStartDatePicker?.close();
    this.occurrenceEndDatePicker?.close();
  }

  private closeAllSelects() {
    this.matSelectComponents?.forEach((selectRef) => {
      if (selectRef.panelOpen) {
        selectRef.close();
      }
    });
  }

  private normalizeDateValueForApi(value: any) {
    const parsed = this.parseInputDate(value);
    if (!parsed) {
      return '';
    }
    return this.formatDateToInput(parsed);
  }

  private normalizeDateInputValue(value: any) {
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return null;
      }
      const date = new Date(value);
      date.setHours(0, 0, 0, 0);
      return date;
    }
    const text = String(value || '').trim();
    if (!text) {
      return null;
    }
    const compact = text.replace(/\s+/g, '');
    const bySeparators = /^(\d{2})[\/\-\.](\d{2})[\/\-\.](\d{4})$/.exec(compact);
    if (bySeparators) {
      const day = Number(bySeparators[1]);
      const month = Number(bySeparators[2]);
      const year = Number(bySeparators[3]);
      const date = new Date(year, month - 1, day);
      if (
        date.getFullYear() === year
        && date.getMonth() === month - 1
        && date.getDate() === day
      ) {
        date.setHours(0, 0, 0, 0);
        return date;
      }
      return null;
    }
    const digitsOnly = compact.replace(/\D/g, '');
    if (/^\d{8}$/.test(digitsOnly)) {
      const day = Number(digitsOnly.slice(0, 2));
      const month = Number(digitsOnly.slice(2, 4));
      const year = Number(digitsOnly.slice(4, 8));
      const date = new Date(year, month - 1, day);
      if (
        date.getFullYear() === year
        && date.getMonth() === month - 1
        && date.getDate() === day
      ) {
        date.setHours(0, 0, 0, 0);
        return date;
      }
      return null;
    }
    return null;
  }

  private defaultDigitalAdmissionTouchedState(): Record<DigitalAdmissionFieldKey, boolean> {
    return {
      cpf: false,
      full_name: false,
      hired_at: false,
      personal_email: false
    };
  }

  private resetDigitalAdmissionValidationState() {
    this.digitalAdmissionTouched = this.defaultDigitalAdmissionTouchedState();
    this.digitalAdmissionSubmitAttempted = false;
  }

  private isDigitalAdmissionStatus(status?: string | null) {
    const normalized = String(status || '').toUpperCase();
    return this.digitalAdmissionStatuses.has(normalized);
  }

  editRole(role: ControlDpRole) {
    this.editingRole = role;
    this.roleModalTab = 'cadastro';
    this.roleForm = {
      name: role.name,
      base_salary: role.base_salary !== null && role.base_salary !== undefined ? String(role.base_salary) : ''
    };
    this.showRoleModal = true;
  }

  openImportModal(importType: string) {
    this.importType = importType;
    this.importFileName = '';
    this.importPreviewRows = [];
    this.importHeaders = [];
    this.importFeedback = '';
    this.importErrorRows = [];
    this.showImportModal = true;
  }

  openFilterModal() {
    if (!this.feedbackTemplates.length && !this.feedbackTemplatesLoading) {
      this.loadFeedbackTemplates();
    }
    this.showFilterModal = true;
  }

  closeFilterModal() {
    this.showFilterModal = false;
  }

  applyFilters() {
    this.currentPage = 1;
    this.digitalAdmissionPage = 1;
    if (this.activeTab === 'dashboard') {
      this.loadMoodThermometer(this.moodPeriodMonths);
      if (this.showDashboardFeedback) {
        this.loadFeedbackDashboard();
      }
    }
    this.showFilterModal = false;
  }

  clearFilters() {
    this.filterSectorId = '';
    this.filterDepartmentId = '';
    this.dashboardFilter = 'ALL';
    this.dashboardFilterTouched = false;
    this.feedbackDashboardTemplateId = '';
    this.currentPage = 1;
    this.digitalAdmissionPage = 1;
    if (this.activeTab === 'dashboard') {
      this.loadMoodThermometer(this.moodPeriodMonths);
      this.loadFeedbackDashboard();
    }
  }

  closeModals() {
    this.clearAlertRuleMapRefreshTimeout();
    this.showEmployeeModal = false;
    this.showOccurrenceModal = false;
    this.showHistoryModal = false;
    this.showRoleModal = false;
    this.roleModalTab = 'cadastro';
    this.showAlertModal = false;
    this.showFeedbackModal = false;
    this.showEmployeeFeedbackModal = false;
    this.editingAlertRuleId = null;
    this.alertRuleMapUrl = '';
    this.alertRuleMapEmbedUrl = null;
    this.alertRuleMapLoading = false;
    this.editingRole = null;
    this.showImportModal = false;
    this.feedbackSaving = false;
    this.feedbackEditorLoading = false;
    this.feedbackTemplatesLoading = false;
    this.feedbackError = '';
    this.feedbackTemplatesError = '';
    this.feedbackModalTab = 'cadastro';
    this.feedbackEditingSurveyId = null;
    this.feedbackEditingTemplate = null;
    this.employeeFeedbackTarget = null;
    this.employeeFeedbackTab = 'REGISTRO';
    this.employeeFeedbackRecords = [];
    this.employeeFeedbackQuestions = [];
    this.employeeFeedbackAnswers = {};
    this.employeeFeedbackForm = this.defaultEmployeeFeedbackForm();
    this.employeeFeedbackError = '';
    this.employeeFeedbackLoading = false;
    this.employeeFeedbackQuestionsLoading = false;
    this.employeeFeedbackSaving = false;
    this.clearOccurrenceAttachment();
    this.clearEmployeePhotoPreview();
    this.employeePhotoUploading = false;
    this.resetEmployeeDocsState();
    this.employeeModalSection = 'personal';
    this.occurrenceModalSection = 'data';
    this.occurrenceHistoryLoading = false;
  }

  setRoleModalTab(tab: RoleModalTab) {
    this.roleModalTab = tab;
  }

  isEmployeeModalSectionOpen(section: EmployeeModalSectionKey) {
    return this.employeeModalSection === section;
  }

  setEmployeeModalSection(section: EmployeeModalSectionKey, event?: Event) {
    event?.preventDefault();
    this.employeeModalSection = this.employeeModalSection === section ? null : section;
  }

  isOccurrenceModalSectionOpen(section: OccurrenceModalSectionKey) {
    return this.occurrenceModalSection === section;
  }

  setOccurrenceModalSection(section: OccurrenceModalSectionKey, event?: Event) {
    event?.preventDefault();
    this.occurrenceModalSection = this.occurrenceModalSection === section ? null : section;
  }

  loadEmployeeExtras(employeeId: number) {
    if (!this.tenantId) return;
    this.service.listOccurrences(this.tenantId, employeeId).subscribe({
      next: (rows) => (this.occurrences = rows || []),
      error: () => (this.occurrences = [])
    });
    this.service.listAttachments(this.tenantId, employeeId).subscribe({
      next: (rows) => (this.attachments = rows || []),
      error: () => (this.attachments = [])
    });
    this.loadEmployeeDocuments(employeeId);
    this.loadHistory(employeeId);
  }

  loadEmployeeDocuments(employeeId: number) {
    if (!this.tenantId) {
      return;
    }
    this.employeeDocsLoading = true;
    this.employeeDocsError = '';
    this.service.getEmployeeDocuments(this.tenantId, employeeId, this.userId || undefined).subscribe({
      next: (response: ControlDpEmployeeDocumentsResponse) => {
        this.applyEmployeeDocsResponse(response);
        this.employeeDocsLoading = false;
      },
      error: (error) => {
        this.employeeDocsLoading = false;
        this.employeeDocsError = error?.error?.error || 'Não foi possível carregar os documentos do ControlDocs.';
      }
    });
  }

  loadHistory(employeeId: number) {
    if (!this.tenantId) return;
    const tenantId = this.tenantId;
    forkJoin({
      historyRows: this.service.listHistory(tenantId, employeeId).pipe(catchError(() => of([] as ControlDpHistory[]))),
      occurrenceRows: this.service.listOccurrences(tenantId, employeeId).pipe(catchError(() => of([] as ControlDpOccurrence[]))),
      alertRouteRows: this.service
        .listEmployeeAdmissionAlertRoutes(tenantId, employeeId)
        .pipe(catchError(() => of([] as ControlDpAdmissionAlertRoute[])))
    }).subscribe({
      next: ({ historyRows, occurrenceRows, alertRouteRows }) => {
        const occurrences = occurrenceRows || [];
        const alertRoutes = alertRouteRows || [];
        if (!occurrences.length) {
          this.history = this.buildHistoryTimeline(historyRows || [], [], [], alertRoutes);
          this.historyPage = 1;
          return;
        }
        const attachmentRequests = occurrences.map((occurrence) =>
          this.service
            .listOccurrenceAttachments(tenantId, occurrence.id)
            .pipe(catchError(() => of([] as ControlDpOccurrenceAttachment[])))
        );
        forkJoin(attachmentRequests).subscribe({
          next: (attachmentGroups) => {
            this.history = this.buildHistoryTimeline(historyRows || [], occurrences, attachmentGroups || [], alertRoutes);
            this.historyPage = 1;
          },
          error: () => {
            this.history = this.buildHistoryTimeline(historyRows || [], occurrences, [], alertRoutes);
            this.historyPage = 1;
          }
        });
      },
      error: () => {
        this.history = [];
      }
    });
  }

  historyItemTrackBy(_: number, item: HistoryTimelineItem) {
    return item.id;
  }

  saveEmployee() {
    if (!this.tenantId || !this.userId) return;
    const payload = {
      tenant_id: this.tenantId,
      created_by: this.userId,
      updated_by: this.userId,
      status: this.employeeForm.status,
      full_name: this.employeeForm.full_name,
      employee_code: this.employeeForm.employee_code,
      cpf: this.employeeForm.cpf,
      rg: this.employeeForm.rg,
      birth_date: this.normalizeDateValueForApi(this.employeeForm.birth_date),
      birth_location: this.employeeForm.birth_location,
      race: this.employeeForm.race,
      gender: this.employeeForm.gender,
      marital_status: this.employeeForm.marital_status,
      mother_name: this.employeeForm.mother_name,
      father_name: this.employeeForm.father_name,
      education_level: this.employeeForm.education_level,
      personal_email: this.employeeForm.personal_email,
      corporate_email: this.employeeForm.corporate_email,
      phone: this.employeeForm.phone,
      cep: this.employeeForm.cep,
      street: this.employeeForm.street,
      number: this.employeeForm.number,
      complement: this.employeeForm.complement,
      neighborhood: this.employeeForm.neighborhood,
      city: this.employeeForm.city,
      state: this.employeeForm.state,
      emergency_contact_name: this.employeeForm.emergency_contact_name,
      emergency_contact_phone: this.employeeForm.emergency_contact_phone,
      hired_at: this.normalizeDateValueForApi(this.employeeForm.hired_at),
      esocial_code: this.employeeForm.esocial_code,
      ctps_number: this.employeeForm.ctps_number,
      pis: this.employeeForm.pis,
      pcd: this.employeeForm.pcd ? 1 : 0,
      cbo_code: this.employeeForm.cbo_code,
      bank: this.employeeForm.bank,
      bank_branch: this.employeeForm.bank_branch,
      bank_account: this.employeeForm.bank_account,
      bank_account_type: this.employeeForm.bank_account_type,
      contract_type: this.employeeForm.contract_type,
      salary: this.toNumberOrNull(this.employeeForm.salary),
      role_id: this.toNumberOrNull(this.employeeForm.role_id),
      sector_id: this.toNumberOrNull(this.employeeForm.sector_id),
      department_id: this.toNumberOrNull(this.employeeForm.department_id),
      location_id: this.toNumberOrNull(this.employeeForm.location_id),
      photo_url: this.employeeForm.photo_url || null,
      notes: this.employeeForm.notes
    };
    const request$ = this.selectedEmployee
      ? this.service.updateEmployee(this.selectedEmployee.id, payload)
      : this.service.createEmployee(payload);
    request$.subscribe({
      next: () => {
        this.loadEmployees();
        this.loadDashboard();
        this.closeModals();
      }
    });
  }

  saveOccurrence() {
    if (!this.tenantId || !this.userId || !this.selectedEmployee) return;
    if (this.occurrenceForm.type !== 'PROMOTION') {
      this.occurrenceForm.promoted_role_id = '';
      this.occurrenceForm.promoted_salary = '';
    }
    if (this.occurrenceForm.type !== 'CERTIFICATE') {
      this.occurrenceForm.certificate_institution = '';
      this.occurrenceForm.certificate_course = '';
      this.occurrenceForm.certificate_modality = '';
      this.occurrenceForm.certificate_knowledge_area = '';
    }
    const payload = {
      ...this.occurrenceForm,
      type: this.occurrenceForm.type,
      start_date: this.normalizeDateValueForApi(this.occurrenceForm.start_date),
      end_date: this.normalizeDateValueForApi(this.occurrenceForm.end_date),
      certificate_institution: String(this.occurrenceForm.certificate_institution || '').trim() || null,
      certificate_course: String(this.occurrenceForm.certificate_course || '').trim() || null,
      certificate_modality: String(this.occurrenceForm.certificate_modality || '').trim() || null,
      certificate_knowledge_area: String(this.occurrenceForm.certificate_knowledge_area || '').trim() || null,
      tenant_id: this.tenantId,
      employee_id: this.selectedEmployee.id,
      created_by: this.userId
    };
    this.service.createOccurrence(payload).subscribe({
      next: (occurrence) => {
        if (this.occurrenceAttachmentFile) {
          this.service
            .uploadOccurrenceAttachment(this.tenantId!, occurrence.id, this.userId!, this.occurrenceAttachmentFile)
            .subscribe({
              next: () => this.finalizeOccurrenceSave(),
              error: () => this.finalizeOccurrenceSave()
            });
          return;
        }
        this.finalizeOccurrenceSave();
      }
    });
  }

  saveRole() {
    if (!this.tenantId || !this.roleForm.name.trim()) return;
    const payload = {
      tenant_id: this.tenantId,
      name: this.roleForm.name.trim(),
      base_salary: this.roleForm.base_salary ? Number(this.roleForm.base_salary) : null,
      created_by: this.userId
    };
    const request$ = this.editingRole
      ? this.service.updateRole(this.editingRole.id, payload)
      : this.service.createRole(payload);
    request$.subscribe({
      next: () => {
        this.loadRoles();
        this.closeModals();
      }
    });
  }

  async saveFeedback() {
    if (!this.tenantId || !this.userId) {
      this.feedbackError = 'Sessão inválida para salvar feedback.';
      return;
    }
    const title = String(this.feedbackForm.title || '').trim();
    const description = String(this.feedbackForm.description || '').trim();
    const introText = String(this.feedbackForm.intro_text || '').trim();
    const sectorIds = (Array.isArray(this.feedbackForm.sector_ids) ? this.feedbackForm.sector_ids : [])
      .map((value: any) => Number(value))
      .filter((value: number) => Number.isFinite(value) && value > 0);
    const departmentIds = (Array.isArray(this.feedbackForm.department_ids) ? this.feedbackForm.department_ids : [])
      .map((value: any) => Number(value))
      .filter((value: number) => Number.isFinite(value) && value > 0);

    if (!title) {
      this.feedbackError = 'Informe o título do feedback.';
      return;
    }
    if (!sectorIds.length || !departmentIds.length) {
      this.feedbackError = 'Selecione ao menos um setor e um departamento.';
      return;
    }
    if (!this.feedbackQuestions.length) {
      this.feedbackError = 'Adicione pelo menos uma pergunta.';
      return;
    }

    for (const [index, question] of this.feedbackQuestions.entries()) {
      if (!String(question.title || '').trim()) {
        this.feedbackError = `Pergunta ${index + 1}: informe o enunciado.`;
        return;
      }
      if (this.feedbackQuestionNeedsOptions(question.question_type)) {
        const validOptions = question.options.map((option) => String(option?.label || '').trim()).filter(Boolean);
        if (validOptions.length < 2) {
          this.feedbackError = `Pergunta ${index + 1}: adicione ao menos duas opções válidas.`;
          return;
        }
      }
    }

    this.feedbackSaving = true;
    this.feedbackError = '';
    try {
      let surveyId = this.feedbackEditingSurveyId;
      if (surveyId) {
        await firstValueFrom(
          this.controlquestion.updateSurvey(surveyId, {
            tenant_id: this.tenantId,
            updated_by: this.userId,
            title,
            description: description || null,
            intro_text: introText || null,
            outro_text: null,
            status: 'DRAFT',
            intranet_enabled: true
          })
        );
      } else {
        const survey = await firstValueFrom(
          this.controlquestion.createSurvey({
            tenant_id: this.tenantId,
            created_by: this.userId,
            updated_by: this.userId,
            title,
            description: description || null,
            intro_text: introText || null,
            outro_text: null,
            status: 'DRAFT',
            intranet_enabled: true
          })
        );
        surveyId = Number(survey.id);
      }

      if (!surveyId) {
        throw new Error('survey_id inválido.');
      }

      await this.syncFeedbackQuestions(surveyId);

      await firstValueFrom(
        this.service.createFeedbackTemplate({
          tenant_id: this.tenantId,
          survey_id: surveyId,
          title,
          description: description || null,
          sector_ids: sectorIds,
          department_ids: departmentIds,
          created_by: this.userId,
          updated_by: this.userId
        })
      );

      this.feedbackSaving = false;
      this.loadFeedbackTemplates();
      this.closeFeedbackModal();
    } catch (error: any) {
      this.feedbackSaving = false;
      this.feedbackError = error?.error?.error || 'Não foi possível salvar o feedback.';
    }
  }

  onAttachmentSelected(event: Event) {
    if (!this.tenantId || !this.userId || !this.selectedEmployee) return;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.service.uploadAttachment(this.tenantId, this.selectedEmployee.id, this.userId, file).subscribe({
      next: () => this.loadEmployeeExtras(this.selectedEmployee!.id)
    });
  }

  onAttachmentDrop(event: DragEvent) {
    event.preventDefault();
    this.attachmentDragging = false;
    if (!this.tenantId || !this.userId || !this.selectedEmployee) return;
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    this.service.uploadAttachment(this.tenantId, this.selectedEmployee.id, this.userId, file).subscribe({
      next: () => this.loadEmployeeExtras(this.selectedEmployee!.id)
    });
  }

  onAttachmentDragOver(event: DragEvent) {
    event.preventDefault();
    this.attachmentDragging = true;
  }

  onAttachmentDragLeave(event: DragEvent) {
    event.preventDefault();
    this.attachmentDragging = false;
  }

  setEmployeeDocsTab(tab: EmployeeDocsTab) {
    this.employeeDocsTab = tab;
    this.employeeDocsPreview = null;
  }

  onEmployeeDocsSelected(event: Event, category: EmployeeDocsCategoryKey) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    this.uploadEmployeeDoc(category, file);
  }

  onEmployeeDocsDragOver(event: DragEvent, category: EmployeeDocsCategoryKey) {
    event.preventDefault();
    this.employeeDocsDraggingCategory = category;
  }

  onEmployeeDocsDragLeave(event: DragEvent, category: EmployeeDocsCategoryKey) {
    event.preventDefault();
    if (this.employeeDocsDraggingCategory !== category) {
      return;
    }
    this.employeeDocsDraggingCategory = null;
  }

  onEmployeeDocsDrop(event: DragEvent, category: EmployeeDocsCategoryKey) {
    event.preventDefault();
    this.employeeDocsDraggingCategory = null;
    const file = event.dataTransfer?.files?.[0];
    if (!file) {
      return;
    }
    this.uploadEmployeeDoc(category, file);
  }

  uploadEmployeeDoc(category: EmployeeDocsCategoryKey, file: File) {
    if (!this.tenantId || !this.userId || !this.selectedEmployee) {
      return;
    }
    this.employeeDocsUploadingCategory = category;
    this.employeeDocsError = '';
    this.service.uploadEmployeeDocument(this.tenantId, this.selectedEmployee.id, this.userId, category, file).subscribe({
      next: (response) => {
        const normalizedCategory = this.normalizeEmployeeDocsCategory(response?.category || category);
        this.employeeDocsByCategory[normalizedCategory] = [response.file, ...this.employeeDocsByCategory[normalizedCategory]];
        this.employeeDocsUploadingCategory = null;
        if (!this.employeeDocsPreview) {
          this.openEmployeeDocPreview(response.file);
        }
      },
      error: (error) => {
        this.employeeDocsUploadingCategory = null;
        this.employeeDocsError = error?.error?.error || 'Não foi possível enviar o documento.';
      }
    });
  }

  removeEmployeeDoc(category: EmployeeDocsCategoryKey, file: ControlDpEmployeeDocumentFile) {
    if (!this.tenantId || !this.userId || !this.selectedEmployee || !file?.id) {
      return;
    }
    this.employeeDocsDeletingFileId = file.id;
    this.employeeDocsError = '';
    this.service.deleteEmployeeDocument(this.tenantId, this.selectedEmployee.id, file.id, this.userId).subscribe({
      next: () => {
        this.employeeDocsByCategory[category] = this.employeeDocsByCategory[category].filter((item) => item.id !== file.id);
        this.employeeDocsDeletingFileId = null;
        if (this.employeeDocsPreview?.source === 'controldocs' && this.employeeDocsPreview.path === file.file_path) {
          this.employeeDocsPreview = null;
        }
      },
      error: (error) => {
        this.employeeDocsDeletingFileId = null;
        this.employeeDocsError = error?.error?.error || 'Não foi possível remover o documento.';
      }
    });
  }

  employeeDocTrackBy(_: number, file: ControlDpEmployeeDocumentFile) {
    return file.id;
  }

  openEmployeeDocPreview(file: ControlDpEmployeeDocumentFile) {
    this.employeeDocsPreview = {
      name: file.name,
      path: file.file_path,
      file_type: file.file_type,
      created_at: file.uploaded_at || null,
      source: 'controldocs'
    };
  }

  openAttachmentPreview(file: ControlDpAttachment) {
    this.employeeDocsPreview = {
      name: file.file_name,
      path: file.file_path,
      file_type: file.file_type || null,
      created_at: file.created_at || null,
      source: 'attachments'
    };
  }

  closeEmployeeDocPreview() {
    this.employeeDocsPreview = null;
  }

  canPreviewFile(fileName?: string | null, fileType?: string | null): boolean {
    return this.isImageFile(fileName, fileType) || this.isPdfFile(fileName, fileType);
  }

  isImageFile(fileName?: string | null, fileType?: string | null): boolean {
    const type = String(fileType || '').toLowerCase();
    if (type.startsWith('image/')) {
      return true;
    }
    const name = String(fileName || '').toLowerCase();
    return ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp'].some((ext) => name.endsWith(ext));
  }

  isPdfFile(fileName?: string | null, fileType?: string | null): boolean {
    const type = String(fileType || '').toLowerCase();
    if (type.includes('pdf')) {
      return true;
    }
    const name = String(fileName || '').toLowerCase();
    return name.endsWith('.pdf');
  }

  formatAttachmentUrl(path?: string | null) {
    if (!path) return '#';
    if (path.startsWith('http')) return path;
    return `${this.apiBase}${path}`;
  }

  fileThumbLabel(fileName?: string | null, fileType?: string | null): string {
    if (this.isPdfFile(fileName, fileType)) {
      return 'PDF';
    }
    const name = String(fileName || '').toLowerCase();
    const extMatch = /\.([a-z0-9]{2,5})$/.exec(name);
    if (extMatch?.[1]) {
      return extMatch[1].toUpperCase();
    }
    return 'ARQ';
  }

  getEmployeePhotoUrl() {
    if (this.employeePhotoLocalPreviewUrl) {
      return this.employeePhotoLocalPreviewUrl;
    }
    const photoUrl = this.employeeForm?.photo_url;
    if (!photoUrl) {
      return this.defaultEmployeeAvatar;
    }
    if (
      String(photoUrl).startsWith('blob:')
      || String(photoUrl).startsWith('data:')
      || String(photoUrl).startsWith('http')
    ) {
      return photoUrl;
    }
    return this.formatAttachmentUrl(photoUrl);
  }

  onEmployeePhotoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !String(file.type || '').startsWith('image/')) {
      return;
    }

    this.clearEmployeePhotoPreview();
    this.employeePhotoLocalPreviewUrl = URL.createObjectURL(file);

    if (!this.selectedEmployee || !this.tenantId || !this.userId) {
      return;
    }

    this.employeePhotoUploading = true;
    this.service.uploadAttachment(this.tenantId, this.selectedEmployee.id, this.userId, file).subscribe({
      next: (attachment) => {
        this.employeePhotoUploading = false;
        if (attachment?.file_path) {
          this.employeeForm.photo_url = attachment.file_path;
        }
        this.clearEmployeePhotoPreview();
        this.loadEmployeeExtras(this.selectedEmployee!.id);
      },
      error: () => {
        this.employeePhotoUploading = false;
      }
    });
  }

  occurrenceTypeDisplay(type?: string | null) {
    return this.occurrenceTypeLabel(type);
  }

  occurrencePeriodDisplay(startDate?: string | null, endDate?: string | null) {
    return this.occurrencePeriodLabel(startDate, endDate);
  }

  employeeFeedbackStatusLabel(value?: string | null) {
    const status = String(value || '').toUpperCase();
    if (status === 'REGISTRO') return 'Registro';
    if (status === 'DRAFT') return 'Rascunho';
    if (status === 'SCHEDULED') return 'Agendado';
    if (status === 'APPLIED') return 'Aplicado';
    return '—';
  }

  handleImportFile(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.importFileName = file.name;
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (extension === 'xlsx') {
      const reader = new FileReader();
      reader.onload = () => {
        const data = new Uint8Array(reader.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) as any[][];
        if (!rows.length) {
          this.importHeaders = [];
          this.importPreviewRows = [];
          return;
        }
        const headers = rows[0].map((header) => String(header || '').trim()).filter(Boolean);
        const dateHeaders = new Set(['birth_date', 'hired_at', 'experience_45_days', 'experience_90_days', 'start_date', 'end_date']);
        const bodyRows = rows.slice(1).filter((row) => row.some((cell) => String(cell || '').trim().length));
        this.importHeaders = headers;
        this.importPreviewRows = bodyRows.map((row) => {
          const obj: Record<string, any> = {};
          headers.forEach((header, idx) => {
            const value = row[idx];
            obj[header] = dateHeaders.has(header) ? this.toImportDate(value) : value ?? '';
          });
          return obj;
        });
      };
      reader.readAsArrayBuffer(file);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const content = String(reader.result || '');
      const parsed = this.parseCsv(content);
      this.importHeaders = parsed.headers;
      this.importPreviewRows = parsed.rows;
    };
    reader.readAsText(file);
  }

  confirmImport() {
    if (!this.tenantId || !this.userId || !this.importPreviewRows.length) return;
    this.importLoading = true;
    this.service.createImport({
      tenant_id: this.tenantId,
      created_by: this.userId,
      import_type: this.importType,
      file_name: this.importFileName || null,
      rows: this.importPreviewRows
    }).subscribe({
      next: (response: any) => {
        this.importLoading = false;
        this.importErrorRows = Array.isArray(response?.errors) ? response.errors : [];
        if (this.importErrorRows.length) {
          this.importFeedback = `Importação concluída com ${this.importErrorRows.length} erro(s).`;
          this.loadImports();
          this.loadEmployees();
          this.loadDashboard();
          return;
        }
        this.importFeedback = 'Importação concluída com sucesso.';
        this.loadImports();
        this.loadEmployees();
        this.loadDashboard();
        this.closeModals();
      },
      error: () => {
        this.importLoading = false;
      }
    });
  }

  downloadLookupSheet(type: 'sectors' | 'departments' | 'roles') {
    if (!this.tenantId) {
      return;
    }
    this.exportLoading = true;
    const request$ =
      type === 'sectors'
        ? this.service.listSectorsForExport(this.tenantId)
        : type === 'departments'
          ? this.service.listDepartmentsForExport(this.tenantId)
          : this.service.listRolesForExport(this.tenantId);
    request$.subscribe({
      next: (rows: any[]) => {
        const { fileName, headers } = this.getLookupTemplate(type);
        const data = [headers, ...rows.map((row) => headers.map((header) => row[header] ?? ''))];
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Dados');
        XLSX.writeFile(workbook, fileName);
        this.exportLoading = false;
      },
      error: () => {
        this.exportLoading = false;
      }
    });
  }

  downloadFailedImportRows(imp: ControlDpImport) {
    if (!this.tenantId || !imp?.id) {
      return;
    }
    this.exportLoading = true;
    this.service.listImportFailedRows(this.tenantId, imp.id).subscribe({
      next: (rows) => {
        const baseHeaders = ['row_index', '__error'];
        const payloadKeys = new Set<string>();
        (rows || []).forEach((row) => {
          Object.keys(row || {}).forEach((key) => {
            if (!baseHeaders.includes(key)) {
              payloadKeys.add(key);
            }
          });
        });
        const headers = [...baseHeaders, ...Array.from(payloadKeys)];
        const data = [headers, ...(rows || []).map((row) => headers.map((header) => row?.[header] ?? ''))];
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Erros');
        const fileName = `controldp-import-errors-${imp.id}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        this.exportLoading = false;
      },
      error: () => {
        this.exportLoading = false;
      }
    });
  }

  private parseCsv(content: string) {
    const lines = content.split(/\r?\n/).filter((line) => line.trim().length);
    if (!lines.length) {
      return { headers: [], rows: [] };
    }
    const delimiter = lines[0].includes(';') ? ';' : ',';
    const headers = lines[0].split(delimiter).map((h) => h.trim());
    const rows = lines.slice(1).map((line) => {
      const cols = line.split(delimiter);
      const row: Record<string, any> = {};
      headers.forEach((header, idx) => {
        row[header] = (cols[idx] || '').trim();
      });
      return row;
    });
    return { headers, rows };
  }

  getBarPercent(count: number, max: number) {
    if (!max) {
      return 0;
    }
    return Math.round((count / max) * 100);
  }

  private buildHistoryTimeline(
    historyRows: ControlDpHistory[],
    occurrences: ControlDpOccurrence[],
    occurrenceAttachmentGroups: ControlDpOccurrenceAttachment[][],
    admissionAlertRoutes: ControlDpAdmissionAlertRoute[]
  ): HistoryTimelineItem[] {
    const items: HistoryTimelineItem[] = [];
    const routeViews = this.buildHistoryAlertRouteViews(admissionAlertRoutes || []);
    let hasBoundAlertRoute = false;

    occurrences.forEach((occurrence, index) => {
      const attachments = (occurrenceAttachmentGroups?.[index] || []).map((attachment) => ({
        name: attachment.file_name,
        path: attachment.file_path,
        created_at: attachment.created_at || null
      }));
      const summaryLines: string[] = [];
      const periodLabel = this.occurrencePeriodLabel(occurrence.start_date, occurrence.end_date);
      if (periodLabel) {
        summaryLines.push(periodLabel);
      }
      if (occurrence.description) {
        summaryLines.push(`Descrição: ${occurrence.description}`);
      }
      if (occurrence.notes) {
        summaryLines.push(`Observação: ${occurrence.notes}`);
      }

      items.push({
        id: `occurrence-${occurrence.id}`,
        action_type: 'OCCURRENCE',
        action_label: 'Ocorrência',
        title: this.occurrenceTypeLabel(occurrence.type),
        created_at: occurrence.created_at || occurrence.start_date || null,
        summary_lines: summaryLines.length ? summaryLines : ['Ocorrência registrada sem detalhes adicionais.'],
        attachments,
        alert_routes: []
      });
    });

    (historyRows || []).forEach((row) => {
      const actionType = String(row.action_type || '').toUpperCase();
      const description = String(row.description || '').trim();
      const isOccurrenceLog = actionType === 'OCCURRENCE';
      const isOccurrenceAttachmentLog =
        actionType === 'ATTACHMENT' && description.toLowerCase().startsWith('anexo de ocorrência');
      if (isOccurrenceLog || isOccurrenceAttachmentLog) {
        return;
      }

      const attachments: HistoryAttachmentView[] =
        row.attachment_path
          ? [
              {
                name: row.attachment_name || 'Baixar anexo',
                path: row.attachment_path,
                created_at: row.created_at || null
              }
            ]
          : [];

      const isAddressAlertLog = actionType === 'STATUS_CHANGE'
        && description.toLowerCase().includes('alerta automático de endereço identificado');
      const alertRoutesForItem = isAddressAlertLog && !hasBoundAlertRoute ? routeViews : [];
      if (alertRoutesForItem.length) {
        hasBoundAlertRoute = true;
      }

      items.push({
        id: `history-${row.id}`,
        action_type: actionType,
        action_label: this.historyActionLabel(actionType),
        title: this.historyTitleLabel(actionType),
        created_at: row.created_at || null,
        summary_lines: this.buildHistorySummaryLines(actionType, description),
        attachments,
        alert_routes: alertRoutesForItem
      });
    });

    return items.sort((left, right) => this.toTimestamp(right.created_at) - this.toTimestamp(left.created_at));
  }

  private historyActionLabel(actionType: string): string {
    const normalized = String(actionType || '').toUpperCase();
    const labels: Record<string, string> = {
      CREATED: 'Cadastro',
      UPDATED: 'Atualização',
      OCCURRENCE: 'Ocorrência',
      ATTACHMENT: 'Anexo',
      IMPORT: 'Importação',
      STATUS_CHANGE: 'Status',
      ADMISSION_LINK_REACTIVATED: 'Admissão digital'
    };
    return labels[normalized] || 'Registro';
  }

  private historyTitleLabel(actionType: string): string {
    const normalized = String(actionType || '').toUpperCase();
    const labels: Record<string, string> = {
      CREATED: 'Colaborador cadastrado',
      UPDATED: 'Dados do colaborador atualizados',
      ATTACHMENT: 'Documento/anexo registrado',
      IMPORT: 'Registro de importação',
      STATUS_CHANGE: 'Atualização de status',
      ADMISSION_LINK_REACTIVATED: 'Link de admissão reativado'
    };
    return labels[normalized] || 'Evento registrado';
  }

  private buildHistorySummaryLines(actionType: string, description: string): string[] {
    const raw = String(description || '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!raw) {
      return ['Registro realizado.'];
    }

    const normalizedForSplit = raw.replace(/\s+(CEP:|Validação:|Condição:|Medição:)/gi, ' | $1');
    const splitByPipe = normalizedForSplit
      .split('|')
      .map((part) => this.formatHistorySummaryToken(part))
      .map((part) => this.normalizeAlertSummaryLine(part))
      .filter(Boolean);

    if (splitByPipe.length > 1) {
      const first = splitByPipe.shift() || '';
      const alertMatch = /^(.*?\.)\s*(Regras acionadas:.*)$/i.exec(first);
      if (alertMatch) {
        return [
          this.formatHistorySummaryToken(alertMatch[1]),
          this.formatHistorySummaryToken(alertMatch[2]),
          ...splitByPipe
        ].filter(Boolean);
      }
      return [first, ...splitByPipe].filter(Boolean);
    }

    const normalizedAction = String(actionType || '').toUpperCase();
    if (normalizedAction === 'STATUS_CHANGE') {
      const alertMatch = /^(.*?\.)\s*(Regras acionadas:.*)$/i.exec(raw);
      if (alertMatch) {
        return [
          this.formatHistorySummaryToken(alertMatch[1]),
          this.formatHistorySummaryToken(alertMatch[2])
        ].filter(Boolean);
      }
    }

    return [this.formatHistorySummaryToken(raw)];
  }

  private normalizeAlertSummaryLine(value: string): string {
    const normalized = String(value || '').trim();
    if (!normalized) {
      return '';
    }
    if (/^CEP:/i.test(normalized)) {
      return normalized.replace(/^CEP:/i, 'Referência da regra: CEP');
    }
    if (/^Medição:/i.test(normalized)) {
      return normalized.replace(/^Medição:/i, 'Resultado medido:');
    }
    return normalized;
  }

  private formatHistorySummaryToken(value: string): string {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .replace(/CEP:\s*(\d{5})(\d{3})/gi, 'CEP: $1-$2')
      .replace(/\s+\./g, '.')
      .trim();
  }

  private historyAlertValidationLabel(validationUnitValue: any, travelModeValue: any): string {
    const validationUnit = String(validationUnitValue || '').toUpperCase();
    const base = validationUnit === 'TRAVEL_TIME' ? 'Tempo de deslocamento (min)' : 'Raio (km)';
    if (validationUnit === 'TRAVEL_TIME') {
      const modeLabel = this.optionLabel(this.alertTravelModeOptions, String(travelModeValue || '').toUpperCase());
      return modeLabel ? `${base} · ${modeLabel}` : base;
    }
    return base;
  }

  private historyAlertConditionLabel(operatorValue: any, minValue: any, maxValue: any): string {
    const operator = String(operatorValue || '').toUpperCase();
    const operatorLabel = this.optionLabel(this.alertConditionOptions, operator) || 'Condição';
    const min = this.formatAlertRuleNumeric(minValue);
    if (operator === 'BETWEEN') {
      return `${operatorLabel} ${min} e ${this.formatAlertRuleNumeric(maxValue)}`;
    }
    return `${operatorLabel} ${min}`;
  }

  private buildGoogleDirectionsUrl(originValue: any, destinationValue: any, travelModeValue: any): string {
    const origin = String(originValue || '').trim();
    const destination = String(destinationValue || '').trim();
    if (!origin || !destination) {
      return '';
    }
    const mode = String(travelModeValue || '').toUpperCase();
    const travelmode = mode === 'WALKING' ? 'walking' : mode === 'TRANSIT' ? 'transit' : 'driving';
    return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&travelmode=${travelmode}`;
  }

  private buildHistoryAlertRouteViews(rows: ControlDpAdmissionAlertRoute[]): HistoryAlertRouteView[] {
    return (rows || []).map((row, index) => {
      const id = String(row?.id || `alert-route-${index}`);
      const measuredValue = Number(row?.measured_value);
      const measuredUnitNormalized = String(row?.measured_unit || '').toUpperCase();
      const measuredUnit = measuredUnitNormalized === 'MIN' ? 'min' : 'km';
      const measuredLabel = Number.isFinite(measuredValue)
        ? `${measuredValue.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ${measuredUnit}`
        : 'não calculado';
      const measuredTitle = measuredUnitNormalized === 'MIN' ? 'Tempo A → B' : 'Distância A → B';
      return {
        id,
        alert_name: String(row?.alert_name || 'Regra de alerta'),
        measured_title: measuredTitle,
        measured_label: measuredLabel,
        validation_label: this.historyAlertValidationLabel(row?.validation_unit, row?.travel_mode),
        condition_label: this.historyAlertConditionLabel(row?.condition_operator, row?.condition_value_min, row?.condition_value_max),
        origin_address: String(row?.origin_address || '-'),
        destination_address: String(row?.destination_address || '-'),
        map_path: this.formatMapImageUrl(row?.map_path),
        directions_url: this.buildGoogleDirectionsUrl(row?.origin_address, row?.destination_address, row?.travel_mode),
        created_at: row?.created_at || null
      };
    });
  }

  private formatMapImageUrl(path?: string | null): string {
    const raw = String(path || '').trim();
    if (!raw) {
      return '';
    }
    if (raw.startsWith('http')) {
      return raw;
    }
    if (raw.startsWith('/api/')) {
      return raw;
    }
    if (raw.startsWith('/')) {
      return `${this.apiBase}${raw}`;
    }
    return `${this.apiBase}/${raw}`;
  }

  private occurrenceTypeLabel(type?: string | null): string {
    const normalized = String(type || '').toUpperCase();
    const labels: Record<string, string> = {
      ABSENCE: 'Falta',
      LATE: 'Atraso',
      JUSTIFICATION: 'Justificativa',
      WARNING: 'Advertência escrita',
      WARNING_WRITTEN: 'Advertência escrita',
      WARNING_VERBAL: 'Advertência verbal',
      SUSPENSION: 'Suspensão',
      LEAVE: 'Afastamento',
      CERTIFICATE: 'Certificado',
      PROMOTION: 'Promoção'
    };
    return labels[normalized] || 'Ocorrência';
  }

  private occurrencePeriodLabel(startDate?: string | null, endDate?: string | null): string | null {
    const startLabel = this.toPtBrDate(startDate);
    const endLabel = this.toPtBrDate(endDate);
    if (startLabel && endLabel && startLabel !== endLabel) {
      return `Período: ${startLabel} até ${endLabel}`;
    }
    if (startLabel) {
      return `Data da ocorrência: ${startLabel}`;
    }
    if (endLabel) {
      return `Data da ocorrência: ${endLabel}`;
    }
    return null;
  }

  private toPtBrDate(value?: string | null): string | null {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toLocaleDateString('pt-BR');
  }

  private toPtBrDateTime(value?: string | null): string | null {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toLocaleString('pt-BR');
  }

  private toDateTimeLocalInput(value?: string | null) {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    const hours = `${date.getHours()}`.padStart(2, '0');
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  private toTimestamp(value?: string | null): number {
    if (!value) {
      return 0;
    }
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private aggregateDistribution(items: ControlDpEmployee[], field: keyof ControlDpEmployee, fallback: string) {
    const total = items.length || 1;
    const counts = new Map<string, number>();
    items.forEach((item) => {
      const raw = item[field];
      const label = raw ? String(raw) : fallback;
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([label, count]) => ({
        label,
        count,
        percent: Math.round((count / total) * 100)
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }

  private addDays(date: Date, days: number) {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  }

  private diffInDays(from: Date, to: Date) {
    const ms = to.getTime() - from.getTime();
    return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
  }

  private toImportDate(value: any) {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString().slice(0, 10);
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      const parsed = XLSX.SSF.parse_date_code(value);
      if (parsed) {
        const year = String(parsed.y).padStart(4, '0');
        const month = String(parsed.m).padStart(2, '0');
        const day = String(parsed.d).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '';
      const match = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (match) {
        return `${match[3]}-${match[2]}-${match[1]}`;
      }
      return trimmed;
    }
    return value;
  }

  private getTemplateDefinition(type: string) {
    const templates: Record<string, { headers: string[]; sample: string[]; fileName: string }> = {
      EMPLOYEES: {
        headers: [
          'status',
          'full_name',
          'employee_code',
          'cpf',
          'rg',
          'birth_date',
          'birth_location',
          'race',
          'gender',
          'marital_status',
          'mother_name',
          'father_name',
          'education_level',
          'personal_email',
          'corporate_email',
          'phone',
          'cep',
          'street',
          'number',
          'complement',
          'neighborhood',
          'city',
          'state',
          'emergency_contact_name',
          'emergency_contact_phone',
          'hired_at',
          'esocial_code',
          'ctps_number',
          'pis',
          'pcd',
          'cbo_code',
          'bank',
          'bank_branch',
          'bank_account',
          'bank_account_type',
          'contract_type',
          'role_id',
          'sector_id',
          'department_id',
          'location_id',
          'notes'
        ],
        sample: [
          'ACTIVE',
          'Maria Aparecida',
          'DP-2025-001',
          '123.456.789-00',
          '12.345.678-9',
          '1994-06-12',
          'Sao Paulo - SP',
          'Parda',
          'F',
          'Casado(a)',
          'Ana dos Santos',
          'Jose dos Santos',
          'Ensino superior completo',
          'maria@email.com',
          'maria@empresa.com',
          '(11) 99999-0000',
          '01000-000',
          'Rua das Flores',
          '123',
          'Apto 45',
          'Centro',
          'Sao Paulo',
          'SP',
          'Joao Santos',
          '(11) 98888-1111',
          '2024-02-15',
          'ESOC-9981',
          '1234567',
          '123.45678.90-1',
          '0',
          '2521-05',
          'Banco do Brasil',
          '1234',
          '56789-0',
          'Corrente',
          'CLT',
          '1',
          '13',
          '22',
          '5',
          'Registro inicial'
        ],
        fileName: 'controldp-import-employees.xlsx'
      },
      ABSENCES: {
        headers: ['cpf', 'full_name', 'start_date', 'end_date', 'description', 'notes'],
        sample: ['123.456.789-00', 'Maria Aparecida', '2024-03-01', '2024-03-02', 'Falta medica', 'Atestado entregue'],
        fileName: 'controldp-import-absences.xlsx'
      },
      LATES: {
        headers: ['cpf', 'full_name', 'start_date', 'description', 'notes'],
        sample: ['123.456.789-00', 'Maria Aparecida', '2024-03-05', 'Atraso de 30 minutos', 'Transito intenso'],
        fileName: 'controldp-import-lates.xlsx'
      }
    };
    return templates[type] || templates['EMPLOYEES'];
  }

  private getLookupTemplate(type: 'sectors' | 'departments' | 'roles') {
    if (type === 'departments') {
      return {
        headers: ['id', 'name', 'sector_id'],
        fileName: 'controldp-departments.xlsx'
      };
    }
    if (type === 'roles') {
      return {
        headers: ['id', 'name', 'tenant_id'],
        fileName: 'controldp-roles.xlsx'
      };
    }
    return {
      headers: ['id', 'name'],
      fileName: 'controldp-sectors.xlsx'
    };
  }

  private async syncFeedbackQuestions(surveyId: number) {
    if (!this.tenantId || !this.userId) {
      throw new Error('Sessão inválida.');
    }

    const detail = await firstValueFrom(this.controlquestion.getSurvey(this.tenantId, surveyId));
    const existingQuestions = (Array.isArray(detail?.questions) ? detail.questions : [])
      .filter((question: any) => Number(question.survey_id) === Number(surveyId));
    const existingQuestionMap = new Map<number, any>();
    existingQuestions.forEach((question: any) => {
      const id = Number(question.id);
      if (id) {
        existingQuestionMap.set(id, question);
      }
    });

    const optionRows = Array.isArray(detail?.options) ? detail.options : [];
    const existingOptionsByQuestion = new Map<number, any[]>();
    optionRows.forEach((option: any) => {
      const questionId = Number(option.question_id);
      if (!questionId) return;
      if (!existingOptionsByQuestion.has(questionId)) {
        existingOptionsByQuestion.set(questionId, []);
      }
      existingOptionsByQuestion.get(questionId)!.push(option);
    });

    const keptQuestionIds = new Set<number>();

    for (let questionIndex = 0; questionIndex < this.feedbackQuestions.length; questionIndex += 1) {
      const question = this.feedbackQuestions[questionIndex];
      const type = question.question_type;
      const isChoiceType = this.feedbackQuestionNeedsOptions(type);
      const optionLabels = question.options
        .map((option) => String(option?.label || '').trim())
        .filter(Boolean);

      const questionPayload = {
        tenant_id: this.tenantId,
        question_type: type,
        title: String(question.title || '').trim(),
        description: String(question.description || '').trim() || null,
        media_type: 'NONE',
        media_url: null,
        weight: 0,
        required: question.required,
        display_format: type === 'DROPDOWN' ? 'COLUMN' : 'ROW',
        min_answers: 1,
        max_answers: isChoiceType && type !== 'SINGLE_CHOICE' && type !== 'DROPDOWN'
          ? Math.max(1, optionLabels.length)
          : 1,
        order_index: questionIndex + 1,
        created_by: this.userId,
        updated_by: this.userId
      };

      let questionId = Number(question.id || 0);
      if (questionId && existingQuestionMap.has(questionId)) {
        await firstValueFrom(this.controlquestion.updateQuestion(questionId, questionPayload));
      } else {
        const createdQuestion = await firstValueFrom(
          this.controlquestion.createQuestion(surveyId, questionPayload)
        );
        questionId = Number(createdQuestion.id || 0);
      }

      if (!questionId) {
        continue;
      }
      question.id = questionId;
      keptQuestionIds.add(questionId);

      const existingOptions = (existingOptionsByQuestion.get(questionId) || [])
        .slice()
        .sort((a: any, b: any) => (Number(a.order_index || 0) - Number(b.order_index || 0)) || (Number(a.id || 0) - Number(b.id || 0)));
      const existingOptionMap = new Map<number, any>();
      existingOptions.forEach((option: any) => {
        const id = Number(option.id);
        if (id) {
          existingOptionMap.set(id, option);
        }
      });

      if (!isChoiceType) {
        for (const option of existingOptions) {
          const optionId = Number(option.id);
          if (optionId) {
            await firstValueFrom(this.controlquestion.deleteOption(this.tenantId, optionId));
          }
        }
        question.options = [];
        continue;
      }

      const normalizedOptions = question.options
        .map((option, index) => ({
          id: option.id ? Number(option.id) : null,
          label: String(option.label || '').trim(),
          order_index: index + 1
        }))
        .filter((option) => !!option.label);

      const keptOptionIds = new Set<number>();
      const nextOptions: FeedbackOptionDraft[] = [];

      for (const option of normalizedOptions) {
        if (option.id && existingOptionMap.has(option.id)) {
          await firstValueFrom(
            this.controlquestion.updateOption(option.id, {
              tenant_id: this.tenantId,
              label: option.label,
              value_text: null,
              image_url: null,
              weight: 0,
              is_other: false,
              order_index: option.order_index
            })
          );
          keptOptionIds.add(option.id);
          nextOptions.push({ id: option.id, label: option.label });
        } else {
          const createdOption = await firstValueFrom(
            this.controlquestion.createOption(questionId, {
              tenant_id: this.tenantId,
              label: option.label,
              value_text: null,
              image_url: null,
              weight: 0,
              is_other: false,
              order_index: option.order_index
            })
          );
          const createdId = Number(createdOption?.id || 0);
          if (createdId) {
            keptOptionIds.add(createdId);
            nextOptions.push({ id: createdId, label: option.label });
          }
        }
      }

      for (const existing of existingOptions) {
        const optionId = Number(existing.id);
        if (!optionId || keptOptionIds.has(optionId)) {
          continue;
        }
        await firstValueFrom(this.controlquestion.deleteOption(this.tenantId, optionId));
      }

      question.options = nextOptions;
    }

    for (const existing of existingQuestions) {
      const questionId = Number(existing.id);
      if (!questionId || keptQuestionIds.has(questionId)) {
        continue;
      }
      await firstValueFrom(this.controlquestion.deleteQuestion(this.tenantId, questionId));
    }
  }

  private defaultFeedbackForm() {
    return {
      title: '',
      description: '',
      intro_text: '',
      sector_ids: [] as string[],
      department_ids: [] as string[]
    };
  }

  private parseNumericIdArray(value: any): number[] {
    if (Array.isArray(value)) {
      return value
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item) && item > 0);
    }
    if (typeof value === 'string') {
      const raw = value.trim();
      if (!raw) {
        return [];
      }
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => Number(item))
            .filter((item) => Number.isFinite(item) && item > 0);
        }
      } catch (_error) {
        return [];
      }
    }
    return [];
  }

  private defaultEmployeeFeedbackForm(): EmployeeFeedbackFormDraft {
    return {
      feedback_id: null,
      feedback_template_id: '',
      notes: '',
      collaborator_notes: '',
      scheduled_for: ''
    };
  }

  private buildFeedbackQuestionDraft(type: FeedbackQuestionType = 'SINGLE_CHOICE'): FeedbackQuestionDraft {
    return {
      id: null,
      title: '',
      description: '',
      question_type: type,
      required: true,
      options: this.feedbackQuestionNeedsOptions(type)
        ? [this.buildFeedbackOptionDraft('Opção 1'), this.buildFeedbackOptionDraft('Opção 2')]
        : []
    };
  }

  private buildFeedbackOptionDraft(label = ''): FeedbackOptionDraft {
    return {
      id: null,
      label
    };
  }

  private defaultEmployeeForm() {
    return {
      status: 'ACTIVE',
      full_name: '',
      photo_url: '',
      employee_code: '',
      cpf: '',
      rg: '',
      birth_date: '',
      birth_location: '',
      race: '',
      gender: 'NOT_INFORMED',
      marital_status: '',
      mother_name: '',
      father_name: '',
      education_level: '',
      personal_email: '',
      corporate_email: '',
      phone: '',
      cep: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      hired_at: '',
      experience_45_days: '',
      experience_90_days: '',
      esocial_code: '',
      ctps_number: '',
      pis: '',
      pcd: 0,
      cbo_code: '',
      bank: '',
      bank_branch: '',
      bank_account: '',
      bank_account_type: '',
      contract_type: '',
      salary: '',
      role_id: '',
      sector_id: '',
      department_id: '',
      location_id: '',
      notes: ''
    };
  }

  fillEmployeeTestData() {
    const roleId = this.roles.length ? this.roles[0].id : '';
    const sectorId = this.sectors.length ? this.sectors[0].id : '';
    const departmentsForSector = sectorId
      ? this.departments.filter((dept) => String(dept.sector_id) === String(sectorId))
      : [];
    const departmentId = departmentsForSector.length ? departmentsForSector[0].id : '';
    const locationsForDepartment = departmentId
      ? this.locations.filter((loc) => String(loc.department_id) === String(departmentId))
      : [];
    const locationId = locationsForDepartment.length ? locationsForDepartment[0].id : '';
    this.employeeForm = {
      ...this.employeeForm,
      status: 'ACTIVE',
      full_name: 'Maria Aparecida dos Santos',
      employee_code: 'DP-2025-001',
      cpf: '123.456.789-00',
      rg: '12.345.678-9',
      birth_date: '1994-06-12',
      birth_location: 'Sao Paulo - SP',
      race: 'Parda',
      gender: 'F',
      marital_status: 'Casado(a)',
      mother_name: 'Ana dos Santos',
      father_name: 'Jose dos Santos',
      education_level: 'Ensino superior completo',
      personal_email: 'maria.santos@email.com',
      corporate_email: 'maria.santos@empresa.com',
      phone: '(11) 99999-0000',
      cep: '01000-000',
      street: 'Rua das Flores',
      number: '123',
      complement: 'Apto 45',
      neighborhood: 'Centro',
      city: 'Sao Paulo',
      state: 'SP',
      emergency_contact_name: 'Joao Santos',
      emergency_contact_phone: '(11) 98888-1111',
      hired_at: '2024-02-15',
      esocial_code: 'ESOC-9981',
      ctps_number: '1234567',
      pis: '123.45678.90-1',
      pcd: 0,
      cbo_code: '2521-05',
      bank: 'Banco do Brasil',
      bank_branch: '1234',
      bank_account: '56789-0',
      bank_account_type: 'Corrente',
      contract_type: 'CLT',
      salary: '3200.00',
      role_id: roleId,
      sector_id: sectorId,
      department_id: '',
      location_id: '',
      notes: 'Registro de teste para homologacao.'
    };
    if (roleId) {
      this.onRoleChange();
    }
    if (sectorId) {
      this.onSectorChange();
      this.employeeForm.department_id = departmentId;
      this.onDepartmentChange();
      this.employeeForm.location_id = locationId;
    }
  }

  private toNumberOrNull(value: any) {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    let normalized = value;
    if (typeof normalized === 'string' && normalized.includes(',')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    }
    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private isSalaryEmpty(value: any) {
    return value === null || value === undefined || value === '';
  }

  private toInputDate(value?: string | null) {
    if (!value) {
      return '';
    }
    return this.parseInputDate(value) || '';
  }

  private applyEmployeeDocsResponse(response: ControlDpEmployeeDocumentsResponse) {
    this.employeeDocsByCategory = {
      RG_CPF: [...(response?.files?.RG_CPF || [])],
      ADDRESS: [...(response?.files?.ADDRESS || [])],
      BANK: [...(response?.files?.BANK || [])],
      OTHERS: [...(response?.files?.OTHERS || [])]
    };
    (Object.keys(this.employeeDocsByCategory) as EmployeeDocsCategoryKey[]).forEach((key) => {
      this.employeeDocsByCategory[key].sort((a, b) => {
        const ta = new Date(a.uploaded_at || '').getTime();
        const tb = new Date(b.uploaded_at || '').getTime();
        return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
      });
    });
  }

  private resetEmployeeDocsState() {
    this.employeeDocsByCategory = this.createEmptyEmployeeDocsByCategory();
    this.employeeDocsDraggingCategory = null;
    this.employeeDocsUploadingCategory = null;
    this.employeeDocsDeletingFileId = null;
    this.employeeDocsLoading = false;
    this.employeeDocsError = '';
    this.employeeDocsPreview = null;
  }

  private createEmptyEmployeeDocsByCategory(): Record<EmployeeDocsCategoryKey, ControlDpEmployeeDocumentFile[]> {
    return {
      RG_CPF: [],
      ADDRESS: [],
      BANK: [],
      OTHERS: []
    };
  }

  private normalizeEmployeeDocsCategory(value: string): EmployeeDocsCategoryKey {
    const normalized = String(value || '').toUpperCase();
    if (normalized === 'RG_CPF' || normalized === 'ADDRESS' || normalized === 'BANK' || normalized === 'OTHERS') {
      return normalized;
    }
    return 'OTHERS';
  }

  private defaultFeedbackDashboard(): ControlDpFeedbackDashboard {
    return {
      summary: {
        total: 0,
        draft: 0,
        scheduled: 0,
        applied: 0,
        unique_employees: 0,
        applied_rate: 0,
        last_applied_at: null
      },
      templates: [],
      monthly: [],
      departments: [],
      recent_applied: [],
      insights_template_id: null,
      insights_template_title: null,
      question_insights: [],
      question_insights_message: null
    };
  }

  private defaultOccurrenceForm() {
    return {
      type: 'ABSENCE',
      start_date: '',
      end_date: '',
      description: '',
      notes: '',
      promoted_role_id: '',
      promoted_salary: '',
      certificate_institution: '',
      certificate_course: '',
      certificate_modality: '',
      certificate_knowledge_area: ''
    };
  }

  private defaultDigitalAdmissionForm() {
    return {
      cpf: '',
      full_name: '',
      birth_date: '',
      hired_at: '',
      personal_email: ''
    };
  }

  private defaultAlertRuleForm() {
    return {
      alert_name: '',
      location_type: 'CEP',
      location_value: '',
      validation_unit: 'RADIUS_KM',
      travel_mode: '',
      condition_operator: 'GREATER_THAN',
      condition_value_min: '',
      condition_value_max: '',
      action_type: 'GENERATE_ALERT',
      active: 1
    };
  }

  private refreshAlertRuleMapPreview(immediate = false) {
    if (!this.showAlertModal || !this.tenantId || this.alertRuleForm.location_type !== 'CEP') {
      this.alertRuleMapUrl = '';
      this.alertRuleMapEmbedUrl = null;
      this.alertRuleMapLoading = false;
      return;
    }
    this.clearAlertRuleMapRefreshTimeout();
    const run = () => {
      const cepDigits = this.normalizeCepDigits(String(this.alertRuleForm.location_value || ''));
      if (cepDigits.length !== 8) {
        this.alertRuleMapUrl = '';
        this.alertRuleMapEmbedUrl = null;
        this.alertRuleMapLoading = false;
        return;
      }
      const mapQuery = `${cepDigits}, Brasil`;
      const mapUrl = `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=14&output=embed`;
      this.alertRuleMapLoading = true;
      this.alertRuleMapUrl = mapUrl;
      this.alertRuleMapEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(mapUrl);
    };
    if (immediate) {
      run();
      return;
    }
    this.alertRuleMapRefreshTimeout = setTimeout(run, 450);
  }

  private clearAlertRuleMapRefreshTimeout() {
    if (this.alertRuleMapRefreshTimeout) {
      clearTimeout(this.alertRuleMapRefreshTimeout);
      this.alertRuleMapRefreshTimeout = null;
    }
  }

  onOccurrenceTypeChange() {
    if (this.occurrenceForm.type !== 'PROMOTION') {
      this.occurrenceForm.promoted_role_id = '';
      this.occurrenceForm.promoted_salary = '';
    }
    if (this.occurrenceForm.type !== 'CERTIFICATE') {
      this.occurrenceForm.certificate_institution = '';
      this.occurrenceForm.certificate_course = '';
      this.occurrenceForm.certificate_modality = '';
      this.occurrenceForm.certificate_knowledge_area = '';
    }
  }

  onOccurrenceFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.occurrenceAttachmentFile = file;
    this.occurrenceAttachmentName = file.name;
    this.occurrenceAttachmentDragging = false;
  }

  onOccurrenceFileDrop(event: DragEvent) {
    event.preventDefault();
    this.occurrenceAttachmentDragging = false;
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    this.occurrenceAttachmentFile = file;
    this.occurrenceAttachmentName = file.name;
  }

  onOccurrenceDragOver(event: DragEvent) {
    event.preventDefault();
    this.occurrenceAttachmentDragging = true;
  }

  onOccurrenceDragLeave(event: DragEvent) {
    event.preventDefault();
    this.occurrenceAttachmentDragging = false;
  }

  clearOccurrenceAttachment() {
    this.occurrenceAttachmentFile = null;
    this.occurrenceAttachmentName = '';
    this.occurrenceAttachmentDragging = false;
  }

  private finalizeOccurrenceSave() {
    if (!this.selectedEmployee) {
      this.closeModals();
      return;
    }
    this.loadEmployeeExtras(this.selectedEmployee.id);
    this.loadDashboardOccurrences();
    this.closeModals();
  }

  private resolveLeaveReasonLabel(occurrence: ControlDpOccurrence) {
    const text = `${occurrence.description || ''} ${occurrence.notes || ''}`.toLowerCase();
    if (/(doen[cç]a|tratamento|m[eé]dic|sa[úu]de|atestado)/.test(text)) {
      return 'Doença ou tratamento';
    }
    if (/(maternidade|paternidade|gestante|licen[cç]a matern|licen[cç]a patern)/.test(text)) {
      return 'Licença maternidade/paternidade';
    }
    if (/(n[aã]o remunerada|sem remunera[cç][aã]o|licen[cç]a sem vencimento)/.test(text)) {
      return 'Licença não remunerada';
    }

    return 'Afastamento sem motivo detalhado';
  }

  private clearEmployeePhotoPreview() {
    if (this.employeePhotoLocalPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.employeePhotoLocalPreviewUrl);
    }
    this.employeePhotoLocalPreviewUrl = '';
  }

  private getSavedTab(): TabKey | null {
    try {
      const value = localStorage.getItem(this.tabStorageKey) as TabKey | null;
      return value === 'imports' || value === 'collaborators' || value === 'dashboard' || value === 'digitalAdmissions'
        ? value
        : null;
    } catch (_error) {
      return null;
    }
  }
}
